/* ============================================================
   customization-modal.js
   --------------------------------------------------------------
   PDP customization modal — vanilla JS, no dependencies.
   Loaded only on Prod_Customizable PDPs via add-to-cart.liquid.

   Phase 2 scope (this session):
     - Parse JSON blob on init
     - window.PDPCV global API (open/close/step/submit/state/data)
     - Trigger wiring: CUST2-03 CTA + sidebar "Start Your Custom Request" button
     - Open/close with backdrop/X/Esc + sidebar layering
     - 4-step navigation + indicator fill animation
     - Draft cache to localStorage (7d TTL, schema v1)
     - "Draft saved ✓" fade indicator (2s)
     - Apply copy strings from data.copy to [data-pdpcv-copy] elements
     - Step 2: phone field reveals when Phone preferred + pill toggle
     - Step 3: summary stub (contact info only — Phase 3 populates sub-form data)

   Phase 3+ stubs (do nothing yet):
     - renderDimensions / renderFinishing / renderFeatures
     - Real submitForm (currently advances to step 4 without /contact POST)

   See canonical plan §"Implementation conventions" + §"Locked decisions"
   for the spec.
   ============================================================ */
(function () {
  'use strict';

  // ---------- Constants ----------
  var CACHE_VERSION = 2; // bumped Phase 3: dimensions seeded, finishing shape, features array structure
  var CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  var FADE_MS = 240;

  // v2.5 fix (2026-05-13): on submit, the form also posts (in parallel) to this
  // Fly.io webhook which writes a structured record to Airtable. The URL is the
  // only thing surfaced client-side — all Airtable credentials live as Fly secrets.
  // Origin allow-list + per-IP rate limit gate the endpoint server-side.
  var CUSTOM_REQUEST_WEBHOOK_URL = 'https://arc-custom-requests.fly.dev/v1/submit';

  // v2.5 fix (2026-05-13): retain real File objects in a runtime-only ref so they
  // can be sent to the webhook on submit. state.step3.attachments holds metadata
  // ({name,size,type,idbKey}) because state is JSON-serialized into the cache;
  // the actual binary lives in IndexedDB keyed by idbKey, hydrated back into
  // attachmentFiles on modal open so refresh/navigate-away doesn't lose files.
  var attachmentFiles = [];

  // ---------- IndexedDB attachment store ----------
  // Persists real File/Blob across page reloads so reattaching is unnecessary.
  // Schema: one DB, one object store keyed by idbKey on each state.step3.attachments entry.
  // Entries are deleted on per-file remove + on successful submission (clearCache).
  // Orphans (modal abandoned, cache expired) are swept by idbGarbageCollect on init.
  var IDB_NAME = 'pdpcv-attachments';
  var IDB_STORE = 'files';
  var idbPromise = null;
  function openIdb() {
    if (idbPromise) return idbPromise;
    if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IDB unavailable'));
    idbPromise = new Promise(function (resolve, reject) {
      var req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE, { keyPath: 'key' });
        }
      };
      req.onsuccess = function () { resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
    return idbPromise;
  }
  function idbPut(key, file) {
    return openIdb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put({ key: key, blob: file, name: file.name, type: file.type, savedAt: Date.now() });
        tx.oncomplete = function () { resolve(); };
        tx.onerror = function () { reject(tx.error); };
      });
    });
  }
  function idbGet(key) {
    return openIdb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(IDB_STORE, 'readonly');
        var req = tx.objectStore(IDB_STORE).get(key);
        req.onsuccess = function () {
          var rec = req.result;
          if (!rec) return resolve(null);
          // Reconstruct a File (preferred — preserves .name) from the stored Blob.
          try {
            resolve(new File([rec.blob], rec.name, { type: rec.type }));
          } catch (e) {
            // Older browsers — fall back to Blob with the original name attached.
            var b = new Blob([rec.blob], { type: rec.type });
            b.name = rec.name; // best-effort
            resolve(b);
          }
        };
        req.onerror = function () { reject(req.error); };
      });
    });
  }
  function idbDelete(key) {
    return openIdb().then(function (db) {
      return new Promise(function (resolve) {
        var tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE)['delete'](key);
        tx.oncomplete = function () { resolve(); };
        tx.onerror = function () { resolve(); }; // best-effort
      });
    }).catch(function () { /* IDB unavailable — nothing to delete */ });
  }
  function idbGarbageCollect() {
    // Drop entries older than CACHE_TTL_MS so abandoned uploads don't accumulate.
    return openIdb().then(function (db) {
      return new Promise(function (resolve) {
        var cutoff = Date.now() - CACHE_TTL_MS;
        var tx = db.transaction(IDB_STORE, 'readwrite');
        var store = tx.objectStore(IDB_STORE);
        var cur = store.openCursor();
        cur.onsuccess = function (e) {
          var c = e.target.result;
          if (!c) return resolve();
          if (c.value && c.value.savedAt && c.value.savedAt < cutoff) {
            store['delete'](c.value.key);
          }
          c['continue']();
        };
        cur.onerror = function () { resolve(); };
      });
    }).catch(function () { /* best-effort */ });
  }
  function hydrateAttachmentFilesFromIdb() {
    var entries = (state.step3 && state.step3.attachments) || [];
    attachmentFiles = new Array(entries.length).fill(null);
    if (!entries.length) return Promise.resolve();
    return Promise.all(entries.map(function (entry, idx) {
      if (!entry || !entry.idbKey) return null;
      return idbGet(entry.idbKey).then(function (file) {
        if (file) attachmentFiles[idx] = file;
      }).catch(function () { /* missing — entry will be null */ });
    }));
  }
  // Fire orphan cleanup on init (non-blocking, best-effort).
  idbGarbageCollect();

  // ---------- Bootstrap: data + root ----------
  var dataEl = document.getElementById('pdpcv-modal-data');
  if (!dataEl) return;
  var data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    if (window.console) console.error('[PDPCV] Failed to parse modal data JSON:', e);
    return;
  }

  var root = document.querySelector('[data-pdpcv-root]');
  if (!root) return;

  // ---------- Apply copy from data.copy[key] to [data-pdpcv-copy] elements ----------
  var copy = (data && data.copy) || {};
  document.querySelectorAll('[data-pdpcv-copy]').forEach(function (el) {
    var key = el.getAttribute('data-pdpcv-copy');
    if (copy[key] !== undefined && copy[key] !== null) {
      el.textContent = copy[key];
    }
  });

  // v2.5 iter16: Step 1 heading is "Customize the {firstWord} design" (e.g. "Customize the Belford design").
  // First-word-only per A — consistent with cust2-03 card eyebrow.
  // H2 has data-pdpcv-product-title (no data-pdpcv-copy) so copy-applier doesn't wipe it.
  var titleEl = document.querySelector('[data-pdpcv-product-title]');
  if (titleEl && data.product && data.product.title) {
    titleEl.textContent = buildHeadingTitle(data.product);
  }

  // v2.5 iter16: renderAckList now substitutes {contact_method} from state.step2.preferredContact.
  // Called on initial load AND on every setStep(4) entry so the line reflects the current selection.
  function renderAckList() {
    var listEl = document.querySelector('[data-pdpcv-ack-list]');
    if (!listEl) return;
    var items = (copy && copy.step4WhatHappensList) || [];
    if (!Array.isArray(items)) {
      items = String(items).split(/\r?\n/).filter(function (s) { return s.trim().length > 0; });
    }
    var method = (state && state.step2 && state.step2.preferredContact === 'phone') ? 'phone' : 'email';
    listEl.innerHTML = items.map(function (t) {
      var line = String(t).replace(/\{contact_method\}/g, method);
      return '<li>' + escapeHtml(line) + '</li>';
    }).join('');
  }
  renderAckList();

  function singularizeProductType(type) {
    // Round 5 iter5: A wants "Conference Table" not "Conference Tables" in the modal title.
    // Strip a trailing single 's' (preserves 'ss', e.g. theoretical "Glass" stays "Glass").
    if (!type) return type;
    var trimmed = type.trim();
    if (trimmed.length >= 2 && trimmed.charAt(trimmed.length - 1) === 's' && trimmed.charAt(trimmed.length - 2) !== 's') {
      return trimmed.slice(0, -1);
    }
    return trimmed;
  }
  function getModelName(p) {
    // Returns the first segment of product.title before a divider (· | -), used by the H2 heading
    // and the variation-line "You are customizing X:" prefix.
    var title = (p && p.title) || '';
    var sepIdx = title.indexOf('·');
    if (sepIdx === -1) sepIdx = title.indexOf('|');
    if (sepIdx === -1) sepIdx = title.indexOf(' - ');
    return sepIdx > 0 ? title.slice(0, sepIdx).trim() : title.trim();
  }
  function getModelFirstWord(p) {
    // v2.5 iter16: first word of the model segment (e.g. "Belford" from "Belford Executive Desk")
    var name = getModelName(p);
    return name ? name.split(/\s+/)[0] : '';
  }
  function buildHeadingTitle(p) {
    // A 2026-06-09 patch: ONLY the scratch flow (customization page →
    // 'Start from scratch' → pick furniture type) overrides the title
    // to copy.step1Heading ('Customize Your <Furniture Type>').
    // PDPs (Mercer etc.) keep the original 'Customize the {firstWord}
    // design' behaviour exactly. The scratch path sets
    // window.CUST_SCRATCH_FLOW=true before injecting the modal.
    if (typeof window !== 'undefined' && window.CUST_SCRATCH_FLOW && typeof copy !== 'undefined' && copy && copy.step1Heading) return copy.step1Heading;
    var firstWord = getModelFirstWord(p);
    if (firstWord) return 'Customize the ' + firstWord + ' design';
    return (p && p.title) || 'Customize';
  }

  // Show selected variant options (e.g. "5'3" width / Right Return / Midnight Black")
  renderSelectedVariation();

  // ---------- State + cache ----------
  var CACHE_KEY = 'pdpcv-modal-draft::' + ((data.product && data.product.handle) || 'unknown');

  function defaultState() {
    return {
      version: CACHE_VERSION,
      savedAt: Date.now(),
      // v2.5 (2026-05-13): set ONCE on first saveCache after defaultState (in saveCache below).
      // Drives the Airtable "Time to Submit (minutes)" computation: submittedAt − firstSavedAt.
      firstSavedAt: null,
      currentStep: 1,
      step1: {
        dimensionsChecked: false,
        finishingChecked: false,
        featuresChecked: false,
        dimensions: null,
        // v2.5 iter22: `samples` is the user-requested sample list (max 5 entries: {code, name, image}).
        finishing: { primary: null, accent: null, samples: [] },
        features: []
      },
      // v2.5 iter39: address fields appear when 1+ samples requested in Step 1; required at submit when shown.
      step2: { name: '', preferredContact: 'email', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '' },
      step3: { extras: '', attachments: [] }
    };
  }

  // Round 5 iter3: max files + size guards. Files held client-side only (no backend yet).
  var MAX_ATTACHMENTS = 5;
  var MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB

  var state = defaultState();

  function loadCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed.version !== CACHE_VERSION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      if (Date.now() - parsed.savedAt > CACHE_TTL_MS) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveCache() {
    try {
      var now = Date.now();
      state.savedAt = now;
      // v2.5 (2026-05-13): firstSavedAt is set exactly once — on the first saveCache
      // after defaultState. Persists across modal close/reopen via the cache.
      if (state.firstSavedAt == null) state.firstSavedAt = now;
      // Strip non-serializable File objects before persisting; only metadata persists.
      var snapshot = JSON.parse(JSON.stringify(state, function (k, v) {
        if (v && typeof v === 'object' && v instanceof File) return undefined;
        return v;
      }));
      localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot));
      flashDraftSaved(); // Round 5 iter5: short "Draft saved" toast on every user change
    } catch (e) { /* storage full or disabled — ignore */ }
  }

  function clearCache() {
    try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
    // v2.5 (2026-05-13): also reset the runtime-only file refs AND drop persisted
    // IDB blobs — those files now live in Airtable.
    var entries = (state.step3 && state.step3.attachments) || [];
    entries.forEach(function (entry) {
      if (entry && entry.idbKey) idbDelete(entry.idbKey);
    });
    attachmentFiles = [];
  }

  // ---------- Toast system (Round 5 iter5) ----------
  // Three flavors share the .pdpcv-draft-saved footer element:
  //   - 'draft'   — short "Draft saved" feedback, fires on user change (2s)
  //   - 'success' — "File uploaded successfully…" (3s)
  //   - 'error'   — red, "Error: <msg>" on Next-click validation failure (2.5s)
  var toastTimer = null;
  var draftSavedSuppressed = true; // suppressed during initial cache restore (no false-flash on open)
  function flashToast(message, type, duration) {
    var el = document.querySelector('[data-pdpcv-draft-saved]');
    if (!el) return;
    var span = el.querySelector('span[data-pdpcv-copy]') || el.querySelector('span');
    if (span) span.textContent = message;
    // Reset all variant classes; apply the active one.
    el.classList.remove('is-draft', 'is-success', 'is-error', 'is-limit-toast');
    el.classList.add('is-' + type);
    el.hidden = false;
    el.setAttribute('data-pdpcv-visible', '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.removeAttribute('data-pdpcv-visible');
      setTimeout(function () {
        el.hidden = true;
        el.classList.remove('is-draft', 'is-success', 'is-error');
      }, FADE_MS);
    }, duration);
  }
  function flashUploadToast() {
    var msg = (data && data.copy && data.copy.fileUploaded) || "File uploaded successfully. You'll see it in the Step 3: Summary.";
    flashToast(msg, 'success', 3000);
  }
  function flashDraftSaved() {
    if (draftSavedSuppressed) return;
    var msg = (data && data.copy && data.copy.draftSaved) || 'Draft saved';
    flashToast(msg, 'draft', 2000);
  }
  function flashErrorToast(reason, where) {
    var prefix = (data && data.copy && data.copy.errorPrefix) || 'Error';
    var msg = prefix + ': ' + reason + (where ? ' — ' + where : '');
    flashToast(msg, 'error', 2500);
  }

  // ---------- Restore form fields from cache ----------
  function restoreFormFromState() {
    var nameInput = document.querySelector('[data-pdpcv-input="name"]');
    if (nameInput) nameInput.value = state.step2.name || '';
    var emailInput = document.querySelector('[data-pdpcv-input="email"]');
    if (emailInput) emailInput.value = state.step2.email || '';
    var phoneInput = document.querySelector('[data-pdpcv-input="phone"]');
    if (phoneInput) phoneInput.value = state.step2.phone || '';
    // v2.5 iter39: restore address fields. Visibility (.pdpcv-step2-address[hidden]) is set
    // by refreshAddressVisibility() at Step 2 entry — see setStep().
    ['address1', 'address2', 'city', 'state', 'zip'].forEach(function (field) {
      var el = document.querySelector('[data-pdpcv-input="' + field + '"]');
      if (el) el.value = state.step2[field] || '';
    });
    // Round 5 iter5: extras lives in both Step 1 and Step 3 — sync both on restore.
    document.querySelectorAll('[data-pdpcv-input="extras"]').forEach(function (el) {
      el.value = state.step3.extras || '';
    });

    var prefValue = state.step2.preferredContact || 'email';
    // Round 5 iter3: preferred-contact is now a <select> (was pills)
    var prefSelect = document.querySelector('[data-pdpcv-pref-select]');
    if (prefSelect) prefSelect.value = prefValue;
    togglePhoneField();

    document.querySelectorAll('[data-pdpcv-toggle]').forEach(function (input) {
      var subKey = input.getAttribute('data-pdpcv-toggle');
      var checked = !!state.step1[subKey + 'Checked'];
      var collapsed = !!state.step1[subKey + 'Collapsed'];
      input.checked = checked;
      var section = document.querySelector('[data-pdpcv-section="' + subKey + '"]');
      if (section) section.classList.toggle('is-collapsed', collapsed);
      var collapseBtn = document.querySelector('[data-pdpcv-collapse="' + subKey + '"]');
      if (collapseBtn) collapseBtn.setAttribute('aria-expanded', String(!collapsed));
      // Re-render the subform body when a cached state restores it as checked.
      // v2.5 fix (2026-05-13): render whenever the section is CHECKED, not only when
      // checked-and-not-collapsed. If the user collapsed the subform before navigating
      // away, the container is empty on next page load — the chevron-expand handler
      // (line ~2642) only calls setSubformOpen() and never renders, so opening the
      // collapsed subform yields an empty pane stuck at max-height:0. Rendering
      // here when checked ensures content is ready before the user expands.
      // Also clear data-pdpcv-rendered first so a stale "true" marker (BFCache or
      // similar) doesn't short-circuit the render.
      if (checked) {
        var sub = document.querySelector('[data-pdpcv-subform="' + subKey + '"]');
        if (sub) sub.removeAttribute('data-pdpcv-rendered');
        if (subKey === 'dimensions') renderDimensions();
        else if (subKey === 'finishing') renderFinishing();
        else if (subKey === 'features') renderFeatures();
      }
      // Round 5 iter9: open/close subform with no animation during cache restore — snap immediately.
      var subform = document.querySelector('[data-pdpcv-subform="' + subKey + '"]');
      if (subform) {
        var shouldOpen = checked && !collapsed;
        subform.classList.toggle('is-open', shouldOpen);
        subform.style.maxHeight = shouldOpen ? 'none' : '0px';
      }
    });
    // v2.5 (2026-05-13): re-hydrate attachmentFiles from IndexedDB so the modal
    // can re-upload them on submit even after a page reload. Async / best-effort;
    // submitForm awaits this same Promise so a fast user can't beat the load.
    hydrateAttachmentFilesFromIdb();
  }

  // ---------- Step navigation ----------
  function setStep(n) {
    if (typeof n !== 'number' || n < 1 || n > 4) return;

    // Round 5 iter6: validate ALL skipped steps (not just the current one) when navigating
    // forward via indicator clicks. Prevents Step 1 → Step 3 bypass.
    if (n > state.currentStep) {
      for (var s = state.currentStep; s < n; s++) {
        if (s === 1) {
          if (!validateAllDims()) {
            var firstDimErr = document.querySelector('[data-pdpcv-dim-row-error]:not([hidden])');
            var dimMsg = firstDimErr ? (firstDimErr.textContent || '').trim() : 'Please review the dimensions';
            flashErrorToast(dimMsg, 'Step 1 — Dimensions');
            return;
          }
        }
        if (s === 2) {
          if (!validateStep2()) {
            var firstErr = document.querySelector('[data-pdpcv-validation]:not([hidden])');
            if (firstErr) {
              var fieldId = firstErr.getAttribute('data-pdpcv-validation') || '';
              var label = fieldId ? (fieldId.charAt(0).toUpperCase() + fieldId.slice(1)) : 'Contact';
              flashErrorToast(firstErr.textContent || 'Required', 'Step 2 — ' + label);
            } else {
              flashErrorToast('Please complete the contact fields', 'Step 2');
            }
            // Land the user on the step that failed so they can fix it
            if (state.currentStep !== s) state.currentStep = s; // re-render below will catch up
            return;
          }
        }
      }
    }

    // Round 5 iter5: show loader IMMEDIATELY (instant, no fade-in) BEFORE the step swap, so
    // the user never sees the new step's content flicker into place. Then defer the swap to
    // the next animation frame so the loader has actually painted.
    showStepLoader();
    var loaderEl = document.querySelector('[data-pdpcv-step-loader]');
    if (loaderEl) void loaderEl.offsetHeight; // force reflow → paint
    requestAnimationFrame(function () { performStepSwap(n); });
    saveCache();
  }

  function performStepSwap(n) {
    state.currentStep = n;

    // Step containers
    document.querySelectorAll('[data-pdpcv-step]').forEach(function (el) {
      var step = parseInt(el.getAttribute('data-pdpcv-step'), 10);
      if (step === n) {
        el.hidden = false;
        requestAnimationFrame(function () { el.classList.add('is-active'); });
      } else {
        el.classList.remove('is-active');
        setTimeout(function () { if (parseInt(el.getAttribute('data-pdpcv-step'), 10) !== state.currentStep) el.hidden = true; }, FADE_MS);
      }
    });

    // Indicator
    document.querySelectorAll('[data-pdpcv-step-link]').forEach(function (el) {
      var step = parseInt(el.getAttribute('data-pdpcv-step-link'), 10);
      el.classList.remove('is-active', 'is-complete');
      if (step === n) el.classList.add('is-active');
      else if (step < n) el.classList.add('is-complete');
      el.setAttribute('aria-selected', step === n ? 'true' : 'false');
    });

    // Indicator fill bars (2 bars between 3 indicator dots)
    document.querySelectorAll('[data-pdpcv-fill]').forEach(function (bar, i) {
      var fillValue = (i < (n > 3 ? 3 : n) - 1) ? 1 : 0;
      bar.style.setProperty('--fill', fillValue);
    });

    // Footer buttons
    var backBtn = document.querySelector('[data-pdpcv-back]');
    var nextBtn = document.querySelector('[data-pdpcv-next]');
    var submitBtn = document.querySelector('[data-pdpcv-submit]');
    var footer = document.querySelector('.pdpcv-footer');

    if (n === 4) {
      if (footer) footer.style.display = 'none';
    } else {
      if (footer) footer.style.display = '';
      if (backBtn) backBtn.hidden = (n === 1);
      if (nextBtn) nextBtn.hidden = (n === 3);
      if (submitBtn) submitBtn.hidden = (n !== 3);
    }

    // v2.5 iter39: refresh address visibility on Step 2 entry — depends on Step 1 sample count.
    if (n === 2) refreshAddressVisibility();
    if (n === 3) renderSummary();
    if (n === 4) {
      renderAckList(); // v2.5 iter16: re-render so {contact_method} reflects current state.step2 selection
      // v2.5 iter19: kick off ack-logo animation. Animated WebP loops once (libwebp loop_count=1).
      // Force re-decode via cache-buster so it plays from frame 0 every Step 4 entry.
      var ackImg = document.querySelector('[data-pdpcv-ack-img]');
      if (ackImg) {
        var ackSrc = ackImg.dataset.src;
        if (ackSrc) ackImg.src = ackSrc.split('#')[0] + '#t=' + Date.now();
      }
      clearCache(); // ack screen reached — clear draft (matches step 4 success semantics)
    }

    // Measure + animate height; hide loader after the height transition completes.
    requestAnimationFrame(function () {
      measureAndSetStepHeight(n);
      setTimeout(hideStepLoader, 360);
    });
  }

  // Round 5 iter10: loader sits between indicator + footer (overlays only the steps region).
  // Round 5 iter11: extend 16px into the indicator's bottom padding + 16px into the footer's top
  // padding so no sliver of content peeks through at the loader edges.
  function positionStepLoader() {
    var l = document.querySelector('[data-pdpcv-step-loader]');
    var ind = document.querySelector('.pdpcv-indicator');
    var ft = document.querySelector('.pdpcv-footer');
    if (!l) return;
    var indH = ind ? ind.offsetHeight : 0;
    var ftH = ft ? ft.offsetHeight : 0;
    l.style.top = Math.max(0, indH - 16) + 'px';
    l.style.bottom = Math.max(0, ftH - 16) + 'px';
  }
  function showStepLoader() {
    positionStepLoader();
    var l = document.querySelector('[data-pdpcv-step-loader]');
    if (l) l.classList.add('is-visible');
  }
  function hideStepLoader() {
    var l = document.querySelector('[data-pdpcv-step-loader]');
    if (l) l.classList.remove('is-visible');
  }

  // Round 5 iter7: modal frame is now fixed 880×776. Dynamic height-setting on .pdpcv-steps
  // is no-op'd; flex:1 + overflow-y:auto handles scroll naturally inside the fixed frame.
  function measureAndSetStepHeight(_stepN) { /* no-op — modal fixed-size, see CSS .pdpcv-modal */ }

  // C4 Round 5: (i) info-tooltip — hover/focus driven via CSS. JS only populates copy text.
  // Round 5 iter9: also a click toggle so tap on touch shows the popover (hover doesn't fire on tap).
  function initInfoTooltips() {
    var copyRef = data && data.copy;
    var keyFor = function (sec) {
      return 'step1' + sec.charAt(0).toUpperCase() + sec.slice(1) + 'Info';
    };
    document.querySelectorAll('.pdpcv-info-popover[data-pdpcv-info-popover]').forEach(function (pop) {
      var sec = pop.getAttribute('data-pdpcv-info-popover');
      // v2.5 iter25: optional explicit key override for popovers that don't follow the step1{Section}Info pattern (e.g. dim-axis (?)).
      var key = pop.getAttribute('data-pdpcv-info-key') || keyFor(sec);
      if (copyRef && copyRef[key]) pop.textContent = copyRef[key];
    });
    // v2.5 iter25: delegated click — works for dynamically-injected (?) buttons (e.g. dim-axis-header
    // popovers rendered later when the dimensions subform expands), not just init-time ones.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.pdpcv-info-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        var wrap = btn.closest('.pdpcv-info-wrap');
        if (!wrap) return;
        var wasOpen = wrap.classList.contains('is-open');
        document.querySelectorAll('.pdpcv-info-wrap.is-open').forEach(function (w) { w.classList.remove('is-open'); });
        if (!wasOpen) wrap.classList.add('is-open');
        return;
      }
      // Tap-elsewhere closes any open popovers.
      if (e.target.closest('.pdpcv-info-wrap')) return;
      document.querySelectorAll('.pdpcv-info-wrap.is-open').forEach(function (w) { w.classList.remove('is-open'); });
    });
  }

  // ---------- Step 3 summary render (Phase 4 — full state output) ----------
  function renderSummary() {
    var el = document.querySelector('[data-pdpcv-summary]');
    if (!el) return;

    var sections = [];

    // Base model + variant
    if (data.product && data.product.title) {
      var selVariant = getSelectedVariant();
      var variantStr = '';
      if (selVariant && selVariant.title && selVariant.title !== 'Default Title') {
        // v2.5 iter16: prefix with "Initial Variation: " per A.
        variantStr = '<div class="pdpcv-summary-section-value-sub">Initial Variation: ' + escapeHtml(selVariant.title) + '</div>';
      }
      sections.push(
        '<div class="pdpcv-summary-section">' +
          '<div class="pdpcv-summary-section-label">Base Design</div>' +
          '<div class="pdpcv-summary-section-value">' + escapeHtml(data.product.title) + variantStr + '</div>' +
        '</div>'
      );
    }

    // Dimensions
    var dims = state.step1.dimensions;
    if (dims && (dims.primary || dims.secondary)) {
      var unit = dims.unit || 'in';
      ['primary', 'secondary'].forEach(function (role) {
        var axis = dims[role];
        if (!axis) return;
        var axisName = data.product[role + 'AxisName'];
        if (!axisName) return;
        // Round 5 iter13: Executive Desk opt-out — show "Opted out" instead of values.
        if (role === 'secondary' && dims.secondaryOptedOut) {
          sections.push(
            '<div class="pdpcv-summary-section">' +
              '<div class="pdpcv-summary-section-label">' + escapeHtml(axisName) + '</div>' +
              '<div class="pdpcv-summary-section-value">Opted out</div>' +
            '</div>'
          );
          return;
        }
        var rows = [];
        if (axis.widthMm != null)  rows.push('Width: '  + escapeHtml(formatMmAs(axis.widthMm, unit)));
        if (axis.depthMm != null)  rows.push('Depth: '  + escapeHtml(formatMmAs(axis.depthMm, unit)));
        if (axis.heightMm != null) rows.push('Height: ' + escapeHtml(formatMmAs(axis.heightMm, unit)));
        if (!rows.length) return;
        sections.push(
          '<div class="pdpcv-summary-section">' +
            '<div class="pdpcv-summary-section-label">' + escapeHtml(axisName) + '</div>' +
            '<div class="pdpcv-summary-section-value">' + rows.join('<br>') + '</div>' +
          '</div>'
        );
      });
    }

    // Finishing
    var fin = state.step1.finishing;
    if (fin && (fin.primary || fin.accent)) {
      var finLines = [];
      if (fin.primary) {
        finLines.push('Primary: ' + escapeHtml(fin.primary.name) + (fin.primary.code ? ' (' + escapeHtml(fin.primary.code) + ')' : ''));
      }
      if (fin.accent) {
        finLines.push('Accent: '  + escapeHtml(fin.accent.name)  + (fin.accent.code  ? ' (' + escapeHtml(fin.accent.code)  + ')' : ''));
      }
      sections.push(
        '<div class="pdpcv-summary-section">' +
          '<div class="pdpcv-summary-section-label">Finishing</div>' +
          '<div class="pdpcv-summary-section-value">' + finLines.join('<br>') + '</div>' +
        '</div>'
      );
    }

    // v2.5 iter25: Requested Finish Samples — list each requested sample as "Name (Code)".
    var samples = (state.step1.finishing && state.step1.finishing.samples) || [];
    if (samples.length) {
      var sampleLines = samples.map(function (s) {
        return escapeHtml(s.name || '') + (s.code ? ' (' + escapeHtml(s.code) + ')' : '');
      }).join('<br>');
      sections.push(
        '<div class="pdpcv-summary-section">' +
          '<div class="pdpcv-summary-section-label">Requested Finish Samples</div>' +
          '<div class="pdpcv-summary-section-value">' + sampleLines + '</div>' +
        '</div>'
      );
    }

    // Features
    var features = state.step1.features || [];
    if (features.length) {
      var featLines = features.map(function (f) { return '<li>' + escapeHtml(f.name) + '</li>'; }).join('');
      sections.push(
        '<div class="pdpcv-summary-section">' +
          '<div class="pdpcv-summary-section-label">Features</div>' +
          '<ul class="pdpcv-summary-features">' + featLines + '</ul>' +
        '</div>'
      );
    }

    // Contact — labeled rows (Name / Email / Phone keyed left, values right)
    if (state.step2.name || state.step2.email || state.step2.phone) {
      var contactRows = '';
      if (state.step2.name) {
        contactRows += '<div class="pdpcv-summary-contact-row"><span class="pdpcv-summary-contact-key">Name</span><span class="pdpcv-summary-contact-val">' + escapeHtml(state.step2.name) + '</span></div>';
      }
      if (state.step2.email) {
        contactRows += '<div class="pdpcv-summary-contact-row"><span class="pdpcv-summary-contact-key">Email</span><span class="pdpcv-summary-contact-val">' + escapeHtml(state.step2.email) + '</span></div>';
      }
      if (state.step2.preferredContact === 'phone' && state.step2.phone) {
        contactRows += '<div class="pdpcv-summary-contact-row"><span class="pdpcv-summary-contact-key">Phone</span><span class="pdpcv-summary-contact-val">' + escapeHtml(state.step2.phone) + '</span></div>';
      }
      // v2.5 iter39/iter43: shipping address row appears when 1+ samples requested AND any address field filled.
      // iter43: single-line comma-separated format. State + ZIP joined by a space (e.g. "PA 17404").
      var samplesForSummary = (state.step1.finishing && state.step1.finishing.samples) || [];
      if (samplesForSummary.length > 0) {
        var l1 = (state.step2.address1 || '').trim();
        var l2 = (state.step2.address2 || '').trim();
        var c = (state.step2.city || '').trim();
        var st = (state.step2.state || '').trim();
        var z = (state.step2.zip || '').trim();
        var addrSegments = [];
        if (l1) addrSegments.push(l1);
        if (l2) addrSegments.push(l2);
        if (c) addrSegments.push(c);
        var stateZip = [st, z].filter(Boolean).join(' ');
        if (stateZip) addrSegments.push(stateZip);
        if (addrSegments.length) {
          contactRows += '<div class="pdpcv-summary-contact-row"><span class="pdpcv-summary-contact-key">Shipping Address</span><span class="pdpcv-summary-contact-val">' + addrSegments.map(escapeHtml).join(', ') + '</span></div>';
        }
      }
      sections.push(
        '<div class="pdpcv-summary-section">' +
          '<div class="pdpcv-summary-section-label">Contact</div>' +
          '<div class="pdpcv-summary-contact">' + contactRows + '</div>' +
        '</div>'
      );
    }

    if (!sections.length) {
      el.innerHTML = '<div class="pdpcv-summary-empty">No information collected yet. Go back through the steps.</div>';
      return;
    }
    el.innerHTML = '<div class="pdpcv-summary-body">' + sections.join('') + '</div>';
  }

  // Read the currently-selected variant from the PDP's option selectors (size dropdown / return radio / finish card).
  // Priority order: size dropdown -> return radio -> finish card -> null. Returns variant ID as Number.
  function readPdpSelectedVariantId() {
    // Webflow product-header size dropdown
    var sizeDropdown = document.getElementById('size-dropdown');
    if (sizeDropdown && sizeDropdown.options) {
      var opt = sizeDropdown.options[sizeDropdown.selectedIndex];
      if (opt && opt.dataset && opt.dataset.variantId) {
        var id = parseInt(opt.dataset.variantId, 10);
        if (id) return id;
      }
    }
    // Return-desk radio (Webflow product-header)
    var returnRadio = document.querySelector('input[name="Return Desk"]:checked, input[name="return-desk"]:checked');
    if (returnRadio && returnRadio.dataset && returnRadio.dataset.variantId) {
      var id2 = parseInt(returnRadio.dataset.variantId, 10);
      if (id2) return id2;
    }
    // Finish card (ag-finish-card with is-selected) — useful when finish is the only option
    var finishCard = document.querySelector('.ag-finish-card.is-selected');
    if (finishCard && finishCard.dataset && finishCard.dataset.variantId) {
      var id3 = parseInt(finishCard.dataset.variantId, 10);
      if (id3) return id3;
    }
    return null;
  }

  function getSelectedVariant() {
    if (!data.product || !data.product.variants) return null;
    // 1. PDP DOM selectors (reflects user's current choice on the PDP)
    var pdpId = readPdpSelectedVariantId();
    if (pdpId) {
      var fromPdp = data.product.variants.find(function (v) { return v.id === pdpId; });
      if (fromPdp) return fromPdp;
    }
    // 2. URL ?variant= param
    var variantId = null;
    try {
      var params = new URLSearchParams(window.location.search);
      var raw = params.get('variant');
      if (raw) variantId = parseInt(raw, 10);
    } catch (e) {}
    if (variantId) {
      var fromUrl = data.product.variants.find(function (v) { return v.id === variantId; });
      if (fromUrl) return fromUrl;
    }
    // 3. First variant
    return data.product.variants[0] || null;
  }

  // Refresh heading + variation subtitle + finishing defaults to match current variant.
  // Called on init, on openModal, and on PDP-selector change.
  //   opts.resetFinishing=true → clear current preselection so new-variant defaults override
  //     (used when PDP variant changes; treats PDP as source of truth)
  //   opts.resetFinishing=false (default) → apply defaults only when state is empty
  //     (used on init/openModal so cached/manual picks survive)
  function refreshVariantUI(opts) {
    opts = opts || {};
    var p = data && data.product;
    if (!p) return;
    var titleEl = document.querySelector('[data-pdpcv-product-title]');
    if (titleEl && p.title) {
      var prefix = (copy && copy.step1Heading) || 'Customize your';
      titleEl.textContent = buildHeadingTitle(p, prefix);
    }
    renderSelectedVariation();
    if (state && state.step1) {
      if (!state.step1.finishing) state.step1.finishing = { primary: null, accent: null };
      if (opts.resetFinishing) {
        state.step1.finishing.primary = null;
        state.step1.finishing.accent = null;
      }
      if (!state.step1.finishing.primary && !state.step1.finishing.accent) {
        applyFinishingDefaults();
        var finContainer = document.querySelector('[data-pdpcv-subform="finishing"]');
        if (finContainer && finContainer.getAttribute('data-pdpcv-rendered') === 'true') {
          finContainer.removeAttribute('data-pdpcv-rendered');
          renderFinishing();
        }
      }
    }
  }

  function bindPdpSelectorListeners() {
    // PDP-selector change resets finishing state to the new variant's defaults (PDP is source of truth).
    var refresh = function () { setTimeout(function () { refreshVariantUI({ resetFinishing: true }); }, 30); };
    var sizeDropdown = document.getElementById('size-dropdown');
    if (sizeDropdown) sizeDropdown.addEventListener('change', refresh);
    document.querySelectorAll('input[name="Return Desk"], input[name="return-desk"]').forEach(function (r) {
      r.addEventListener('change', refresh);
    });
    // Delegated finish-card click (cards may re-render between page-load and click)
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.ag-finish-card')) refresh();
    });
  }

  function renderSelectedVariation() {
    var el = document.querySelector('[data-pdpcv-selected-variation]');
    if (!el) return;
    var v = getSelectedVariant();
    // Shopify variant.title is already option1 / option2 / option3 (e.g. "5'3\" width / Right Return / Midnight Black").
    // Single-variant products have title "Default Title" — hide in that case.
    if (!v || !v.title || v.title === 'Default Title') {
      el.hidden = true;
      return;
    }
    // Round 5 iter5: prepend "You are customizing [Model]: ..." per A's brief.
    var prefix = (copy && copy.variationPrefix) || 'You are customizing';
    var model = getModelName(data.product);
    el.textContent = prefix + ' ' + model + ': ' + v.title;
    el.hidden = false;
  }

  function lookupFinishByHandle(handle) {
    // Round 5 iter4: variant default metafield stores the metaobject's `supplier_code`
    // field (e.g. `9007`) — NOT the metaobject handle (`flame-walnut`). For most
    // finishings these happened to match (e.g. supplier_code `dark-oak` == handle
    // `dark-oak`), but some metaobjects (Flame Walnut) diverge. So we try both:
    // handle first (legacy match) then code (the supplier_code).
    if (!handle || !data.finishings) return null;
    for (var i = 0; i < data.finishings.length; i++) {
      if (data.finishings[i].handle === handle) return data.finishings[i];
    }
    for (var j = 0; j < data.finishings.length; j++) {
      if (data.finishings[j].code === handle) return data.finishings[j];
    }
    return null;
  }

  function applyFinishingDefaults() {
    var v = getSelectedVariant();
    if (!v) return;
    if (!state.step1.finishing) state.step1.finishing = { primary: null, accent: null };
    if (!state.step1.finishing.primary && v.defaultPrimaryFinishHandle) {
      var p = lookupFinishByHandle(v.defaultPrimaryFinishHandle);
      if (p) state.step1.finishing.primary = p;
    }
    if (!state.step1.finishing.accent && v.defaultAccentFinishHandle) {
      var a = lookupFinishByHandle(v.defaultAccentFinishHandle);
      if (a) state.step1.finishing.accent = a;
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---------- Open / close ----------
  function openModal() {
    var cached = loadCache();
    if (cached) {
      state = cached;
      // v2.5 iter22: backfill samples for caches written before this field existed.
      if (state.step1 && state.step1.finishing && !Array.isArray(state.step1.finishing.samples)) {
        state.step1.finishing.samples = [];
      }
      draftSavedSuppressed = true; // don't flash on restore
      restoreFormFromState();
    } else {
      // Default state already initialized; ensure form is reset
      restoreFormFromState();
    }

    // Refresh heading + subtitle + finishing defaults from CURRENT PDP selection (variant may have changed since init)
    refreshVariantUI();

    root.hidden = false;
    requestAnimationFrame(function () {
      root.setAttribute('data-pdpcv-open', '');
      var modal = root.querySelector('.pdpcv-modal');
      if (modal) modal.focus();
    });

    setStep(state.currentStep || 1);
    // Compensate for the scrollbar width disappearing under overflow:hidden so the page
    // doesn't reflow wider behind the modal (measured live — 0 on overlay-scrollbar systems).
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + 'px';
    document.body.style.overflow = 'hidden';

    // Pre-fetch catalog (finishings + features) from Storefront API so by the time
    // user clicks the Finishing or Features checkbox, data is already cached.
    loadCatalogData().then(function () {
      // If user already opened finishing/features subform during fetch, re-render now.
      var finCheckbox = document.querySelector('[data-pdpcv-toggle="finishing"]');
      if (finCheckbox && finCheckbox.checked) {
        sortedFinishingsCache = null;
        renderFinishing();
      }
      var featCheckbox = document.querySelector('[data-pdpcv-toggle="features"]');
      if (featCheckbox && featCheckbox.checked) {
        renderFeatures();
      }
    });

    // Allow draft-saved flashes after first user change
    setTimeout(function () { draftSavedSuppressed = false; }, 500);
  }

  function closeModal() {
    root.removeAttribute('data-pdpcv-open');
    setTimeout(function () { root.hidden = true; }, FADE_MS);
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  function isOpen() {
    return root.hasAttribute('data-pdpcv-open');
  }

  // Sidebar (modal-three #popupModal) — used for Esc layering
  function isSidebarOpen() {
    var popup = document.getElementById('popupModal');
    return !!(popup && popup.classList.contains('quickship-modal--open'));
  }
  function closeSidebar() {
    var popup = document.getElementById('popupModal');
    if (popup) popup.classList.remove('quickship-modal--open');
    document.body.classList.remove('is-quickship-modal--open');
    document.querySelectorAll('.quickship-modal-type').forEach(function (el) {
      el.classList.remove('active');
    });
  }

  // ---------- Phone field toggle ----------
  function togglePhoneField() {
    var phoneField = document.querySelector('[data-pdpcv-phone-field]');
    if (!phoneField) return;
    phoneField.hidden = state.step2.preferredContact !== 'phone';
  }

  // v2.5 iter39: address block in Step 2 only renders when 1+ samples requested in Step 1.
  // Called on Step 2 entry + after any sample add/remove (so live state stays in sync if user
  // navigates back from Step 2 to Step 1, toggles a sample, then returns).
  function refreshAddressVisibility() {
    var addressBlock = document.querySelector('[data-pdpcv-step2-address]');
    if (!addressBlock) return;
    var samples = (state.step1.finishing && state.step1.finishing.samples) || [];
    var shouldShow = samples.length > 0;
    addressBlock.hidden = !shouldShow;
    if (!shouldShow) {
      // Clear validation messages on hidden fields so a stale error doesn't carry over.
      ['address1', 'city', 'state', 'zip'].forEach(function (f) { clearFieldError(f); });
      return;
    }
    // v2.5 iter41: lazily init Google Places Autocomplete the first time the address block becomes visible.
    initAddressAutocomplete();
  }

  // v2.5 iter41: Google Places Autocomplete for the Step 2 Address line 1 input.
  // SDK loaded lazily (only when address block is visible AND a Places API key is configured).
  // No-op when key is empty — address fields still accept manual input.
  var __pdpcvPlacesLoaderState = 'idle'; // idle | loading | ready | failed
  function initAddressAutocomplete() {
    var key = data && data.googlePlacesApiKey;
    if (!key) return; // Key not configured — leave inputs as plain text fields.
    if (window.google && window.google.maps && window.google.maps.places) {
      bindAddressAutocomplete();
      return;
    }
    if (__pdpcvPlacesLoaderState === 'loading' || __pdpcvPlacesLoaderState === 'ready') return;
    __pdpcvPlacesLoaderState = 'loading';
    // Use the documented async-loader callback approach (suppresses the perf warning).
    window.__pdpcvPlacesReady = function () {
      __pdpcvPlacesLoaderState = 'ready';
      bindAddressAutocomplete();
    };
    var s = document.createElement('script');
    s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
            '&libraries=places&loading=async&callback=__pdpcvPlacesReady';
    s.async = true;
    s.defer = true;
    s.onerror = function () {
      __pdpcvPlacesLoaderState = 'failed';
      if (window.console) console.warn('[PDPCV] Google Places SDK failed to load — address inputs remain manual.');
    };
    document.head.appendChild(s);
  }

  function bindAddressAutocomplete() {
    document.querySelectorAll('[data-pdpcv-address-autocomplete]').forEach(function (input) {
      if (input.__pdpcvPlacesBound) return;
      input.__pdpcvPlacesBound = true;
      try {
        var ac = new google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components']
        });
        ac.addListener('place_changed', function () {
          var place = ac.getPlace();
          if (!place || !place.address_components) return;
          var parts = parseAddressComponents(place.address_components);
          // Update state + sync visible inputs (dispatch 'input' so the field-bind handler also fires).
          state.step2.address1 = parts.address1 || '';
          state.step2.city = parts.city || '';
          state.step2.state = parts.state || '';
          state.step2.zip = parts.zip || '';
          ['address1', 'city', 'state', 'zip'].forEach(function (f) {
            var el = document.querySelector('[data-pdpcv-input="' + f + '"]');
            if (el && el.value !== state.step2[f]) {
              el.value = state.step2[f];
              clearFieldError(f);
            }
          });
          saveCache();
        });
      } catch (e) {
        if (window.console) console.warn('[PDPCV] Places.Autocomplete bind failed:', e);
      }
    });
  }

  function parseAddressComponents(components) {
    var out = { address1: '', city: '', state: '', zip: '' };
    var streetNumber = '';
    var route = '';
    components.forEach(function (c) {
      var t = c.types || [];
      if (t.indexOf('street_number') >= 0) streetNumber = c.long_name;
      else if (t.indexOf('route') >= 0) route = c.long_name;
      else if (t.indexOf('locality') >= 0) out.city = c.long_name;
      // Some US addresses (NYC) use 'sublocality' not 'locality' — fall back to it if locality is empty.
      else if (t.indexOf('sublocality') >= 0 && !out.city) out.city = c.long_name;
      else if (t.indexOf('administrative_area_level_1') >= 0) out.state = c.short_name;
      else if (t.indexOf('postal_code') >= 0) out.zip = c.long_name;
    });
    out.address1 = (streetNumber + ' ' + route).trim();
    return out;
  }

  // ============================================================
  // Phase 3 — Step 1 sub-form rendering (Dimensions / Finishing / Features)
  // ============================================================

  // ---------- Shared helpers: DIM parsing + unit conversion ----------
  function parseDimDisplay(s) {
    if (!s) return {};
    var out = {};
    // "280cm W × 100cm D × 76cm H" or "110.2″ W × 39.3″ D × 29.9″ H"
    var re = /([0-9]+(?:\.[0-9]+)?)\s*(cm|mm|in|″|"|ft|')\s*([WDH])/gi;
    var m;
    while ((m = re.exec(s)) !== null) {
      var unit = m[2].toLowerCase().replace('″', 'in').replace('"', 'in').replace("'", 'ft');
      out[m[3].toUpperCase()] = { value: parseFloat(m[1]), unit: unit };
    }
    return out;
  }

  function toMm(value, unit) {
    if (unit === 'mm') return value;
    if (unit === 'cm') return value * 10;
    if (unit === 'in') return value * 25.4;
    if (unit === 'ft') return value * 304.8;
    return value;
  }

  function formatMmAs(mm, unit) {
    if (mm === null || mm === undefined || isNaN(mm)) return '—';
    if (unit === 'mm') return Math.round(mm) + 'mm';
    if (unit === 'cm') {
      var cm = mm / 10;
      return (Math.abs(cm % 1) < 0.05 ? cm.toFixed(0) : cm.toFixed(1)) + 'cm';
    }
    if (unit === 'in') {
      var inches = Math.round((mm / 25.4) * 2) / 2;
      return (Math.abs(inches % 1) < 0.05 ? inches.toFixed(0) : inches.toFixed(1)) + '″';
    }
    if (unit === 'ft') {
      var totalIn = mm / 25.4;
      var ft = Math.floor(totalIn / 12);
      var rem = Math.round((totalIn - ft * 12) * 2) / 2;
      if (rem === 12) { ft += 1; rem = 0; }
      // Round 5 iter6: use typographic prime ′ and double-prime ″ instead of straight quotes.
      return ft + '′' + (Math.abs(rem % 1) < 0.05 ? rem.toFixed(0) : rem.toFixed(1)) + '″';
    }
    return String(mm);
  }

  function quantizeMm(mm, unit) {
    if (mm === null || mm === undefined || isNaN(mm)) return mm;
    if (unit === 'mm' || unit === 'cm') return Math.round(mm / 10) * 10;
    // imperial: snap to 0.5"
    var inches = Math.round((mm / 25.4) * 2) / 2;
    return inches * 25.4;
  }

  // Round 5 iter11: bounds.min in raw mm may not round-trip through display: e.g. min=563mm
  // displays as 22″ (= 558.8mm) which then fails the < bounds.min check. Snap min UP and max DOWN
  // to the nearest display step (12.7mm = 0.5in for ft/in; 10mm for mm) so the displayed value
  // matches the validation threshold exactly.
  function snapBoundsForUnit(bounds, unit) {
    if (!bounds) return null;
    var stepMm = (unit === 'mm') ? 10 : 12.7;
    return {
      min: Math.ceil(bounds.min / stepMm) * stepMm,
      max: Math.floor(bounds.max / stepMm) * stepMm,
      naturalMin: bounds.naturalMin,
      naturalMax: bounds.naturalMax
    };
  }

  function computeAxisBoundsMm(role, axisKey) {
    var fieldName = role + 'Metric';
    var values = [];
    (data.product.variants || []).forEach(function (v) {
      var parsed = parseDimDisplay(v[fieldName]);
      if (parsed[axisKey] !== undefined) {
        values.push(toMm(parsed[axisKey].value, parsed[axisKey].unit));
      }
    });
    if (!values.length) return null;
    var min = Math.min.apply(null, values);
    var max = Math.max.apply(null, values);
    // Round 5 iter6: widen the max by 56.25% (1.25 × 1.25), then round UP to nearest 100mm.
    // Example: natural max 4200mm → 6562.5mm → 6600mm.
    var widenedMax = max * 1.5625;
    var roundedMax = Math.ceil(widenedMax / 100) * 100;
    return { min: min * 0.75, max: roundedMax, naturalMin: min, naturalMax: max };
  }

  function getCurrentVariantValueMm(role, axisKey) {
    var fieldName = role + 'Metric';
    var variantId = null;
    try {
      var params = new URLSearchParams(window.location.search);
      var raw = params.get('variant');
      if (raw) variantId = parseInt(raw, 10);
    } catch (e) {}
    var variants = data.product.variants || [];
    var target = variantId ? variants.find(function (v) { return v.id === variantId; }) : null;
    if (!target) target = variants[Math.floor(variants.length / 2)];
    if (!target) return null;
    var parsed = parseDimDisplay(target[fieldName]);
    if (parsed[axisKey] !== undefined) {
      return toMm(parsed[axisKey].value, parsed[axisKey].unit);
    }
    return null;
  }

  // ============================================================
  // Storefront API catalog fetch (Phase 4+ pivot)
  // Bypasses Shopify Liquid's hard 50-iter cap on metaobject for-loops
  // (limit:500 + chunked-offset both failed in prior pushes).
  // Single GraphQL query returns all 216 finishings + 17 features.
  // Cached in localStorage 24h.
  // ============================================================
  var CATALOG_CACHE_KEY = 'pdpcv-storefront-catalog::v12'; // VP1 S3c — bumped because the finishings query is now PAGINATED and filtered to sample-backed SL finishes. A ::v11 cache holds the old truncated 189; the key change is what discards it (every read/write below keys solely on this constant, so an old key is simply never read again).
  // VP1 S3a: hard ceiling on the finishings cursor walk. 323 metaobjects at 250/page = 2 pages today; 5 leaves headroom for ~1,250 without letting a server that always answers hasNextPage spin the loop forever.
  var CATALOG_MAX_PAGES = 5;
  var CATALOG_TTL_MS = 24 * 60 * 60 * 1000; // 24h
  var catalogFetchPromise = null;
  var catalogLoaded = false;

  function loadCatalogFromCache() {
    try {
      var raw = localStorage.getItem(CATALOG_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > CATALOG_TTL_MS) {
        localStorage.removeItem(CATALOG_CACHE_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function saveCatalogToCache(payload) {
    try {
      localStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({
        savedAt: Date.now(),
        finishings: payload.finishings,
        features: payload.features
      }));
    } catch (e) { /* storage full or disabled — ignore */ }
  }

  function normalizeFinishings(nodes) {
    return (nodes || [])
      // VP1 S3b: SL *and* sample-backed. `sample_available` is the flag the
      // arc-sample-sync reconciler already maintains, so this filter ADOPTS a
      // rule written elsewhere instead of inventing a second list to drift.
      // Measured 2026-08-31: 323 finishing metaobjects, 262 SL, 123 of those
      // sample-backed. A metaobject with no value for the field comes back as
      // `sampleAvailable: null` (not an empty object), so the read is guarded.
      .filter(function (n) {
        return n && n.supplier && n.supplier.value === 'SL'
          && n.sampleAvailable && n.sampleAvailable.value === 'true';
      })
      .map(function (n) {
        var imgUrl = '';
        if (n.image && n.image.reference && n.image.reference.image && n.image.reference.image.url) {
          imgUrl = n.image.reference.image.url;
        }
        return {
          handle: n.handle || '',
          name: (n.name && n.name.value) || '',
          code: (n.supplierCode && n.supplierCode.value) || '',
          tone: (n.tone && n.tone.value) || 'Other',
          category: (n.category && n.category.value) || '',
          image: imgUrl,
          // v2.5 iter22: description powers the 2s hover tip on cards + the lightbox description line.
          description: (n.description && n.description.value) || ''
        };
      });
  }

  function normalizeFeatures(nodes) {
    return (nodes || [])
      .filter(function (n) { return n && n.supplier && n.supplier.value === 'SL'; })
      .map(function (n) {
        var imgUrl = '';
        if (n.image && n.image.reference && n.image.reference.image && n.image.reference.image.url) {
          imgUrl = n.image.reference.image.url;
        }
        // v2.5 iter20: secondary_images is a list — extract all URLs.
        var secImgs = [];
        if (n.secondaryImages && n.secondaryImages.references && Array.isArray(n.secondaryImages.references.nodes)) {
          n.secondaryImages.references.nodes.forEach(function (node) {
            if (node && node.image && node.image.url) secImgs.push(node.image.url);
          });
        }
        return {
          handle: n.handle || '',
          name: (n.name && n.name.value) || '',
          code: (n.supplierCode && n.supplierCode.value) || '',
          image: imgUrl,
          secondaryImages: secImgs
        };
      })
      // v2.5 iter33: sort features alphabetically by SKU (supplier_code). Was previously API order
      // = metaobject creation time. Empty codes sort to the end so blanks don't break the sequence.
      .sort(function (a, b) {
        var ac = a.code || '￿';
        var bc = b.code || '￿';
        return ac.localeCompare(bc);
      });
  }

  function loadCatalogData() {
    // Already loaded in memory
    if (catalogLoaded) return Promise.resolve({ finishings: data.finishings, features: data.features });

    // Try cache
    var cached = loadCatalogFromCache();
    if (cached && Array.isArray(cached.finishings) && cached.finishings.length) {
      data.finishings = cached.finishings;
      data.features = cached.features || [];
      catalogLoaded = true;
      if (window.console) console.info('[PDPCV] Catalog loaded from cache: ' + data.finishings.length + ' finishings + ' + data.features.length + ' features');
      return Promise.resolve({ finishings: data.finishings, features: data.features });
    }

    // Already fetching?
    if (catalogFetchPromise) return catalogFetchPromise;

    var sfApi = (data && data.storefrontApi) || {};
    if (!sfApi.token || !sfApi.endpoint) {
      if (window.console) console.error('[PDPCV] storefrontApi config missing — cannot fetch catalog');
      return Promise.resolve({ finishings: [], features: [] });
    }

    // Image transform: request 300×300 cropped variants from Shopify CDN instead of full-res originals.
    // Cards render at ~150px max; 300×300 covers retina (2×) + headroom. Saves ~95% of image bandwidth.
    // `description` dropped from query (Round 5 perf pass) — search filters on name/code/tone/category only.
    // v2.5 iter20: features.secondaryImages is now a list.file_reference — `references(first: 10)` returns all.
    // v2.5 iter22: finishings.description added (powers hover tip + lightbox description line).
    // VP1 S3a/S3b: the finishings connection is PAGINATED and its
    // selection carries sample_available. The node selection lives in
    // ONE constant because both the first page and every cursor page
    // must ask for identical fields - two literals drift.
    var FIN_NODES = 'nodes { handle supplier: field(key: "supplier") { value } name: field(key: "name") { value } supplierCode: field(key: "supplier_code") { value } tone: field(key: "finish_tone") { value } category: field(key: "category") { value } description: field(key: "description") { value } sampleAvailable: field(key: "sample_available") { value } image: field(key: "image") { reference { ... on MediaImage { image { url(transform: {maxWidth: 300, maxHeight: 300, crop: CENTER}) } } } } }';
    var FIN_PAGE_INFO = 'pageInfo { hasNextPage endCursor }';
    // The features clause is the byte string that was already shipping,
    // lifted out of the old literal rather than retyped.
    var FEAT_CONN = 'features: metaobjects(type: "features", first: 50) { nodes { handle supplier: field(key: "supplier") { value } name: field(key: "name") { value } supplierCode: field(key: "supplier_code") { value } image: field(key: "image") { reference { ... on MediaImage { image { url(transform: {maxWidth: 600, maxHeight: 600, crop: CENTER}) } } } } secondaryImages: field(key: "secondary_images") { references(first: 10) { nodes { ... on MediaImage { image { url(transform: {maxWidth: 600, maxHeight: 600, crop: CENTER}) } } } } } } } }';
    var query = '{ finishings: metaobjects(type: "finishing", first: 250) { ' + FIN_NODES + ' ' + FIN_PAGE_INFO + ' } ' + FEAT_CONN;
    // Cursor pages ask for the finishings connection ONLY - re-fetching
    // features on every page would be free bandwidth spent on nothing.
    // The cursor rides a GraphQL variable so an opaque cursor can never
    // be string-spliced into the document.
    var finPageQuery = 'query finPage($after: String) { finishings: metaobjects(type: "finishing", first: 250, after: $after) { ' + FIN_NODES + ' ' + FIN_PAGE_INFO + ' } }';

    // VP1 S3a: one transport for the combined first page and for every cursor
    // page. Both throw sites are preserved verbatim, so the outer .catch() below
    // still sees exactly the two error shapes it always saw.
    function sfPost(q, vars) {
      var payload = vars ? { query: q, variables: vars } : { query: q };
      return fetch(sfApi.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': sfApi.token
        },
        body: JSON.stringify(payload)
      })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (json.errors) throw new Error('GraphQL errors: ' + JSON.stringify(json.errors));
        return json;
      });
    }

    catalogFetchPromise = sfPost(query)
    .then(function (json) {
      var finNodes = (json.data.finishings.nodes || []).slice();
      var featNodes = json.data.features.nodes;
      var finPages = 1;

      // Bounded cursor walk. finPages counts the page already in hand, so at
      // most CATALOG_MAX_PAGES - 1 further requests are ever issued. Hitting the
      // bound is ANNOUNCED rather than swallowed: a silently truncated catalog
      // is the exact defect this change exists to remove.
      function nextFinPage(pageInfo) {
        if (!pageInfo || !pageInfo.hasNextPage || !pageInfo.endCursor) return Promise.resolve();
        if (finPages >= CATALOG_MAX_PAGES) {
          if (window.console) console.warn('[PDPCV] finishings pagination hit the ' + CATALOG_MAX_PAGES + '-page bound - catalog may be incomplete');
          return Promise.resolve();
        }
        return sfPost(finPageQuery, { after: pageInfo.endCursor }).then(function (page) {
          var conn = page.data.finishings;
          finNodes = finNodes.concat(conn.nodes || []);
          finPages += 1;
          return nextFinPage(conn.pageInfo);
        });
      }

      return nextFinPage(json.data.finishings.pageInfo).then(function () {
        var fins = normalizeFinishings(finNodes);
        var feats = normalizeFeatures(featNodes);
        data.finishings = fins;
        data.features = feats;
        catalogLoaded = true;
        saveCatalogToCache({ finishings: fins, features: feats });
        if (window.console) console.info('[PDPCV] Storefront API fetch OK: ' + fins.length + ' SL finishings + ' + feats.length + ' SL features (' + finNodes.length + ' finishing metaobjects over ' + finPages + ' page(s))');
        return { finishings: fins, features: feats };
      });
    })
    .catch(function (err) {
      catalogFetchPromise = null; // allow retry
      if (window.console) console.error('[PDPCV] Storefront API fetch failed:', err);
      return { finishings: [], features: [] };
    });

    return catalogFetchPromise;
  }

  // ---------- Dimensions sub-form ----------
  function renderDimensions() {
    var container = document.querySelector('[data-pdpcv-subform="dimensions"]');
    if (!container) return;
    if (container.getAttribute('data-pdpcv-rendered') === 'true') return;

    var product = data.product || {};
    var hasPrimary = !!(product.primaryAxisName && String(product.primaryAxisName).length > 0);
    var hasSecondary = !!(product.secondaryAxisName && String(product.secondaryAxisName).length > 0);

    if (!hasPrimary && !hasSecondary) {
      container.innerHTML = '<div class="pdpcv-subform-empty">This product doesn\'t accept custom dimensions yet.</div>';
      container.setAttribute('data-pdpcv-rendered', 'true');
      return;
    }

    if (!state.step1.dimensions) {
      state.step1.dimensions = { unit: 'in', primary: null, secondary: null };
    }
    var currentUnit = state.step1.dimensions.unit || 'in';

    ['primary', 'secondary'].forEach(function (role) {
      var hasAxis = (role === 'primary') ? hasPrimary : hasSecondary;
      if (!hasAxis) { state.step1.dimensions[role] = null; return; }
      if (state.step1.dimensions[role]) return; // already from cache
      state.step1.dimensions[role] = {
        widthMm:  getCurrentVariantValueMm(role, 'W'),
        depthMm:  getCurrentVariantValueMm(role, 'D'),
        heightMm: getCurrentVariantValueMm(role, 'H')
      };
    });

    var html = '';

    // Unit select row (Round 5 iter2: pills -> select, matches Sort Texture/Tone shape)
    html += '<div class="pdpcv-dim-unit-row">';
    html +=   '<label class="pdpcv-dim-unit-field">';
    html +=     '<span class="pdpcv-dim-unit-label">Unit</span>';
    html +=     '<select class="pdpcv-dim-unit-select" data-pdpcv-dim-unit aria-label="Measurement unit">';
    ['ft', 'in', 'mm'].forEach(function (u) {
      var sel = (u === currentUnit) ? ' selected' : '';
      html +=   '<option value="' + u + '"' + sel + '>' + u.toUpperCase() + '</option>';
    });
    html +=     '</select>';
    html +=   '</label>';
    html += '</div>';

    // Per-axis sections (W/D/H inline in a single row under each axis title — C6 Round 5)
    // Round 5 iter13: Executive Desk products can opt out of return dimensions (the secondary axis).
    var isExecDesk = ((product.productType || '') + '').toLowerCase().indexOf('executive desk') >= 0;
    var optedOut = !!(state.step1.dimensions && state.step1.dimensions.secondaryOptedOut);
    ['primary', 'secondary'].forEach(function (role) {
      var hasAxis = (role === 'primary') ? hasPrimary : hasSecondary;
      if (!hasAxis) return;
      var axisName = product[role + 'AxisName'];
      var optClass = (role === 'secondary' && optedOut) ? ' is-opted-out' : '';
      html += '<div class="pdpcv-dim-axis' + optClass + '" data-pdpcv-axis="' + role + '">';
      html +=   '<div class="pdpcv-dim-axis-header">';
      html +=     '<h3 class="pdpcv-dim-axis-title">' + escapeHtml(axisName) + '</h3>';
      // v2.5 iter25: per-axis (?) helper. Copy resolved server-side per product type bucket;
      // empty copy → skip the (?) (e.g. non-Executive products have no secondary axis copy).
      var infoKey = role === 'primary' ? 'dimAxisInfoPrimary' : 'dimAxisInfoSecondary';
      var infoCopy = (data && data.copy && data.copy[infoKey]) || '';
      if (infoCopy) {
        var popoverSec = role === 'primary' ? 'dimAxisPrimary' : 'dimAxisSecondary';
        html += '<span class="pdpcv-info-wrap pdpcv-dim-axis-info-wrap">';
        html +=   '<button type="button" class="pdpcv-info-btn" data-pdpcv-info="' + popoverSec + '" aria-label="What is this?">';
        html +=     '<svg width="12" height="12" fill="currentColor" aria-hidden="true"><use href="#pdpcv-icon-info"></use></svg>';
        html +=   '</button>';
        html +=   '<span class="pdpcv-info-popover" data-pdpcv-info-popover="' + popoverSec + '" data-pdpcv-info-key="' + infoKey + '" role="tooltip">' + escapeHtml(infoCopy) + '</span>';
        html += '</span>';
      }
      if (role === 'secondary' && isExecDesk) {
        // v2.5 iter16: button text is "Remove Return" (active) / "Include Return" (opted out).
        var btnText = optedOut ? 'Include Return' : 'Remove Return';
        html +=   '<button type="button" class="pdpcv-dim-axis-optout" data-pdpcv-dim-optout aria-pressed="' + (optedOut ? 'true' : 'false') + '">' + btnText + '</button>';
      }
      html +=   '</div>';
      html +=   '<div class="pdpcv-dim-row-group">';
      ['W', 'D', 'H'].forEach(function (axisKey) {
        var bounds = computeAxisBoundsMm(role, axisKey);
        if (!bounds) return;
        var stateKey = (axisKey === 'W' ? 'width' : axisKey === 'D' ? 'depth' : 'height') + 'Mm';
        var currentMm = state.step1.dimensions[role][stateKey];
        if (currentMm === null || currentMm === undefined) {
          currentMm = (bounds.naturalMin + bounds.naturalMax) / 2;
        }
        currentMm = quantizeMm(Math.max(bounds.min, Math.min(bounds.max, currentMm)), currentUnit);
        state.step1.dimensions[role][stateKey] = currentMm;
        var label = axisKey === 'W' ? 'Width' : axisKey === 'D' ? 'Depth' : 'Height';
        html += '<div class="pdpcv-dim-row-col">';
        html +=   '<div class="pdpcv-dim-row" data-pdpcv-dim-row="' + role + ':' + axisKey + '">';
        html +=     '<span class="pdpcv-dim-row-label">' + label + '</span>';
        html +=     '<div class="pdpcv-dim-input-wrap" data-pdpcv-dim-input-wrap="' + role + ':' + axisKey + '">';
        html +=       renderDimInputWrap(role, axisKey, currentMm, currentUnit, axisName + ' ' + label);
        html +=     '</div>';
        html +=   '</div>';
        html +=   '<div class="pdpcv-dim-row-error" data-pdpcv-dim-row-error="' + role + ':' + axisKey + '" hidden></div>';
        html += '</div>';
      });
      html +=   '</div>';
      html += '</div>';
    });

    container.innerHTML = html;
    container.setAttribute('data-pdpcv-rendered', 'true');

    bindDimUnitListeners(container);
    bindDimInputListeners(container);
    bindDimOptOut(container);
    // Round 5 iter13: re-apply opt-out visual state after re-render (e.g. unit switch).
    if (optedOut) applyOptOutDom(true);
  }

  // Round 5 iter13: Executive Desk Return Dimensions opt-out.
  // Toggling on: grays the axis, fills inputs with '—', skips validation. Toggling off: restores values.
  function applyOptOutDom(opted) {
    var section = document.querySelector('.pdpcv-dim-axis[data-pdpcv-axis="secondary"]');
    if (!section) return;
    section.classList.toggle('is-opted-out', !!opted);
    var btn = section.querySelector('[data-pdpcv-dim-optout]');
    if (btn) {
      // v2.5 iter16: "Remove Return" / "Include Return"
      btn.textContent = opted ? 'Include Return' : 'Remove Return';
      btn.setAttribute('aria-pressed', opted ? 'true' : 'false');
    }
    section.querySelectorAll('.pdpcv-dim-input').forEach(function (inp) {
      if (opted) {
        if (!inp.dataset.savedValue) inp.dataset.savedValue = inp.value;
        inp.value = '—';
        inp.disabled = true;
      } else {
        if (inp.dataset.savedValue !== undefined) {
          inp.value = inp.dataset.savedValue;
          delete inp.dataset.savedValue;
        }
        inp.disabled = false;
      }
    });
    // Clear any existing error state on secondary
    section.querySelectorAll('.pdpcv-dim-row-error').forEach(function (e) { e.hidden = true; e.textContent = ''; });
    section.querySelectorAll('.pdpcv-dim-row').forEach(function (r) { r.classList.remove('has-error'); });
  }
  function bindDimOptOut(container) {
    var btn = container.querySelector('[data-pdpcv-dim-optout]');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (!state.step1.dimensions) state.step1.dimensions = { unit: 'in', primary: null, secondary: null };
      var next = !state.step1.dimensions.secondaryOptedOut;
      state.step1.dimensions.secondaryOptedOut = next;
      applyOptOutDom(next);
      saveCache();
    });
  }

  function renderDimInputWrap(role, axisKey, mm, unit, ariaLabel) {
    var prefix = ' data-pdpcv-dim-input data-role="' + role + '" data-axis="' + axisKey + '"';
    var aria = ' aria-label="' + escapeHtml(ariaLabel) + '"';
    if (unit === 'ft') {
      var totalIn = mm / 25.4;
      var ft = Math.floor(totalIn / 12);
      var inches = Math.round((totalIn - ft * 12) * 2) / 2;
      if (inches >= 12) { ft += 1; inches = 0; }
      var inchesStr = (Math.abs(inches % 1) < 0.05) ? String(Math.round(inches)) : inches.toFixed(1);
      var html = '';
      html += '<input type="text" inputmode="decimal" class="pdpcv-dim-input pdpcv-dim-input--ft"' + prefix + ' data-part="ft" value="' + ft + '"' + aria + '>';
      html += '<span class="pdpcv-dim-input-unit">ft</span>';
      html += '<input type="text" inputmode="decimal" class="pdpcv-dim-input pdpcv-dim-input--in"' + prefix + ' data-part="in" value="' + inchesStr + '" aria-label="' + escapeHtml(ariaLabel) + ' inches">';
      html += '<span class="pdpcv-dim-input-unit">in</span>';
      return html;
    }
    var val, valStr;
    if (unit === 'in') {
      val = Math.round((mm / 25.4) * 2) / 2;
      valStr = (Math.abs(val % 1) < 0.05) ? String(Math.round(val)) : val.toFixed(1);
    } else { // mm
      val = Math.round(mm / 10) * 10;
      valStr = String(val);
    }
    // Round 5 iter10: inputmode="decimal" so mobile shows the period key (IN values can be x.5).
    return '<input type="text" inputmode="decimal" class="pdpcv-dim-input"' + prefix + ' value="' + valStr + '"' + aria + '><span class="pdpcv-dim-input-unit">' + unit + '</span>';
  }

  function bindDimUnitListeners(container) {
    // Round 5 iter2: unit picker is now a <select> (was radio pills); listen for change.
    container.querySelectorAll('[data-pdpcv-dim-unit]').forEach(function (el) {
      el.addEventListener('change', function () {
        switchDimUnit(el.value);
      });
    });
  }

  function bindDimInputListeners(container) {
    var debounceMap = {};
    container.querySelectorAll('[data-pdpcv-dim-input]').forEach(function (input) {
      input.addEventListener('input', function () {
        var role = input.dataset.role;
        var axisKey = input.dataset.axis;
        var key = role + ':' + axisKey;
        clearTimeout(debounceMap[key]);
        debounceMap[key] = setTimeout(function () { commitDimRow(role, axisKey); }, 300);
      });
      input.addEventListener('blur', function () {
        var role = input.dataset.role;
        var axisKey = input.dataset.axis;
        commitDimRow(role, axisKey);
      });
    });
  }

  // Read inputs for a row, compute mm value, update state, validate.
  function commitDimRow(role, axisKey) {
    var unit = (state.step1.dimensions && state.step1.dimensions.unit) || 'in';
    var wrap = document.querySelector('[data-pdpcv-dim-input-wrap="' + role + ':' + axisKey + '"]');
    if (!wrap) return false;
    var mm = readDimInputMm(wrap, unit);
    var stateKey = (axisKey === 'W' ? 'width' : axisKey === 'D' ? 'depth' : 'height') + 'Mm';
    if (!state.step1.dimensions[role]) state.step1.dimensions[role] = {};
    state.step1.dimensions[role][stateKey] = mm;
    saveCache();
    return validateDimRow(role, axisKey);
  }

  function readDimInputMm(wrap, unit) {
    if (unit === 'ft') {
      var ftInput = wrap.querySelector('[data-part="ft"]');
      var inInput = wrap.querySelector('[data-part="in"]');
      var ft = parseFloat((ftInput && ftInput.value) || '');
      var inches = parseFloat((inInput && inInput.value) || '');
      if (isNaN(ft) && isNaN(inches)) return null;
      var totalIn = (isNaN(ft) ? 0 : ft) * 12 + (isNaN(inches) ? 0 : inches);
      return totalIn * 25.4;
    }
    var input = wrap.querySelector('.pdpcv-dim-input');
    var raw = parseFloat((input && input.value) || '');
    if (isNaN(raw)) return null;
    return unit === 'in' ? raw * 25.4 : raw;
  }

  function validateDimRow(role, axisKey) {
    var rowEl = document.querySelector('[data-pdpcv-dim-row="' + role + ':' + axisKey + '"]');
    var errorEl = document.querySelector('[data-pdpcv-dim-row-error="' + role + ':' + axisKey + '"]');
    var unit = (state.step1.dimensions && state.step1.dimensions.unit) || 'in';
    // Round 5 iter13: Executive Desk opt-out — when secondary is opted out, treat all secondary rows as valid.
    if (role === 'secondary' && state.step1.dimensions && state.step1.dimensions.secondaryOptedOut) {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
      if (rowEl) rowEl.classList.remove('has-error');
      return true;
    }
    var bounds = computeAxisBoundsMm(role, axisKey);
    var stateKey = (axisKey === 'W' ? 'width' : axisKey === 'D' ? 'depth' : 'height') + 'Mm';
    var mm = state.step1.dimensions[role] && state.step1.dimensions[role][stateKey];
    var label = axisKey === 'W' ? 'Width' : axisKey === 'D' ? 'Depth' : 'Height';
    function showError(msg) {
      if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
      if (rowEl) rowEl.classList.add('has-error');
    }
    function clearError() {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
      if (rowEl) rowEl.classList.remove('has-error');
    }
    if (mm === null || mm === undefined || isNaN(mm)) {
      showError('Enter a number');
      return false;
    }
    if (!bounds) { clearError(); return true; }
    // Round 5 iter11: snap bounds to the current display step so the displayed boundary value
    // round-trips through validation (typing 22.5″ when min snaps to 22.5″ passes; typing 22″
    // when raw min is 563mm = 22.16″ correctly fails because 22.5″ is shown as the floor).
    var snapped = snapBoundsForUnit(bounds, unit);
    var TOLERANCE = 1; // safety against float drift
    if (mm < snapped.min - TOLERANCE || mm > snapped.max + TOLERANCE) {
      var minStr = formatMmAs(snapped.min, unit);
      var maxStr = formatMmAs(snapped.max, unit);
      showError(label + ' must be between ' + minStr + ' and ' + maxStr);
      return false;
    }
    clearError();
    return true;
  }

  function validateAllDims() {
    if (!state.step1 || !state.step1.dimensionsChecked) return true;
    var ok = true;
    ['primary', 'secondary'].forEach(function (role) {
      ['W', 'D', 'H'].forEach(function (axisKey) {
        var row = document.querySelector('[data-pdpcv-dim-row="' + role + ':' + axisKey + '"]');
        if (!row) return;
        if (!validateDimRow(role, axisKey)) ok = false;
      });
    });
    return ok;
  }

  function switchDimUnit(newUnit) {
    if (!state.step1.dimensions) state.step1.dimensions = { unit: newUnit, primary: null, secondary: null };
    var oldUnit = state.step1.dimensions.unit || 'in';
    if (oldUnit === newUnit) return;
    state.step1.dimensions.unit = newUnit;
    var container = document.querySelector('[data-pdpcv-subform="dimensions"]');
    if (!container) return;

    // Round 5 iter2: unit is now a <select>; sync its value (pill-toggle removed).
    var unitSelect = container.querySelector('[data-pdpcv-dim-unit]');
    if (unitSelect) unitSelect.value = newUnit;

    // Re-emit each row's input wrap with new unit's input shape
    container.querySelectorAll('[data-pdpcv-dim-input-wrap]').forEach(function (wrap) {
      var key = wrap.getAttribute('data-pdpcv-dim-input-wrap');
      var parts = key.split(':');
      var role = parts[0], axisKey = parts[1];
      var stateKey = (axisKey === 'W' ? 'width' : axisKey === 'D' ? 'depth' : 'height') + 'Mm';
      var mm = state.step1.dimensions[role] && state.step1.dimensions[role][stateKey];
      if (mm === null || mm === undefined) return;
      var quantized = quantizeMm(mm, newUnit);
      state.step1.dimensions[role][stateKey] = quantized;
      var axisName = (data.product[role + 'AxisName'] || '') + ' ' + (axisKey === 'W' ? 'Width' : axisKey === 'D' ? 'Depth' : 'Height');
      wrap.innerHTML = renderDimInputWrap(role, axisKey, quantized, newUnit, axisName);
    });
    // Rebind input listeners for the freshly-emitted inputs
    bindDimInputListeners(container);
    // Re-validate any error rows with the new unit
    container.querySelectorAll('[data-pdpcv-dim-row]').forEach(function (row) {
      var key = row.getAttribute('data-pdpcv-dim-row');
      var parts = key.split(':');
      validateDimRow(parts[0], parts[1]);
    });
    // Round 5 iter14: re-apply opt-out DOM after re-rendering inputs — otherwise the unit switch
    // overwrites the '—' placeholder with the new-unit numeric value.
    if (state.step1.dimensions && state.step1.dimensions.secondaryOptedOut) {
      applyOptOutDom(true);
    }
    saveCache();
  }

  // ---------- Finishing sub-form ----------
  // Port of primaryFinish/accentFinish state machine from
  // pages/customization/v3/components/app-v3.jsx:43-54 (vanilla JS rewrite, not React).
  var sortedFinishingsCache = null;
  var activeFinishTab = 'primary';

  function sortFinishings(arr) {
    return arr.slice().sort(function (a, b) {
      var ta = (a.tone || 'Other').toLowerCase();
      var tb = (b.tone || 'Other').toLowerCase();
      if (ta === 'other' && tb !== 'other') return 1;
      if (tb === 'other' && ta !== 'other') return -1;
      if (ta !== tb) return ta.localeCompare(tb);
      var ca = (a.category || '').toLowerCase();
      var cb = (b.category || '').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });
  }

  function renderFinishing() {
    var container = document.querySelector('[data-pdpcv-subform="finishing"]');
    if (!container) return;
    if (container.getAttribute('data-pdpcv-rendered') === 'true') return;

    var finishings = (data && data.finishings) || [];

    // Catalog not yet loaded: render loading state, trigger fetch, re-render when done.
    // DO NOT mark rendered=true here so the re-render fires correctly after fetch.
    if (!finishings.length && !catalogLoaded) {
      container.innerHTML = '<div class="pdpcv-subform-empty">Loading finishes…</div>';
      loadCatalogData().then(function () {
        sortedFinishingsCache = null; // invalidate so re-sort runs with fresh data
        renderFinishing(); // re-fire; data.finishings is now populated
        // C9 Round 5: re-measure step height now that subform content is real
        if (state && state.currentStep) {
          requestAnimationFrame(function () { measureAndSetStepHeight(state.currentStep); });
        }
      });
      return;
    }

    if (!finishings.length) {
      container.innerHTML = '<div class="pdpcv-subform-empty">No finishing options available for this product yet.</div>';
      container.setAttribute('data-pdpcv-rendered', 'true');
      return;
    }

    if (!state.step1.finishing) state.step1.finishing = { primary: null, accent: null };
    applyFinishingDefaults();
    if (!sortedFinishingsCache) sortedFinishingsCache = sortFinishings(finishings);
    var sorted = sortedFinishingsCache;

    var html = '';

    // Selected-swatches row (persistent, both slots always visible)
    html += '<div class="pdpcv-fin-selected-row" data-pdpcv-fin-selected-row>';
    html +=   renderSelectedSwatchSlot('primary');
    html +=   renderSelectedSwatchSlot('accent');
    html += '</div>';

    // Controls: sort dropdowns (texture + tone) + search. Active role is set by clicking slot above.
    var textures = uniqueFinishValues('category');
    var tones = uniqueFinishValues('tone');
    html += '<div class="pdpcv-fin-controls">';
    html +=   '<div class="pdpcv-fin-sorts">';
    html +=     '<label class="pdpcv-fin-sort">';
    html +=       '<span class="pdpcv-fin-sort-label">Sort Texture</span>';
    html +=       '<select class="pdpcv-fin-sort-select" data-pdpcv-fin-sort="category">';
    html +=         '<option value="">All</option>';
    textures.forEach(function (t) { html += '<option value="' + escapeHtml(t.toLowerCase()) + '">' + escapeHtml(t) + '</option>'; });
    html +=       '</select>';
    html +=     '</label>';
    html +=     '<label class="pdpcv-fin-sort">';
    html +=       '<span class="pdpcv-fin-sort-label">Sort Tone</span>';
    html +=       '<select class="pdpcv-fin-sort-select" data-pdpcv-fin-sort="tone">';
    html +=         '<option value="">All</option>';
    tones.forEach(function (t) { html += '<option value="' + escapeHtml(t.toLowerCase()) + '">' + escapeHtml(t) + '</option>'; });
    html +=       '</select>';
    html +=     '</label>';
    html +=   '</div>';
    // v2.5 iter22: wrap search input with an (X) clear button (visible only when input has value).
    html +=   '<div class="pdpcv-fin-search-wrap">';
    html +=     '<input type="search" class="pdpcv-fin-search" data-pdpcv-fin-search';
    html +=       ' placeholder="' + escapeHtml((data.copy && data.copy.step1FinishingSearchPlaceholder) || 'Search finishes…') + '"';
    html +=       ' aria-label="Search finishes">';
    html +=     '<button type="button" class="pdpcv-fin-search-clear" data-pdpcv-fin-search-clear hidden aria-label="Clear search">';
    html +=       '<svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
    html +=     '</button>';
    html +=   '</div>';
    html += '</div>';

    // Grid with tone-group dividers
    html += '<div class="pdpcv-fin-grid-wrap" data-pdpcv-fin-grid-wrap>';
    html += renderFinishGrid(sorted);
    html += '</div>';

    // Footer: catalogue download link (enabled when a URL is configured in theme settings)
    // and an upload-to-color-match link that opens the Step 3 file picker.
    var catalogueUrl = (data && data.catalogueUrl) || '';
    html += '<div class="pdpcv-fin-footer">';
    if (catalogueUrl) {
      html += '<a class="pdpcv-fin-link" href="' + escapeHtml(catalogueUrl) + '" target="_blank" rel="noopener" data-pdpcv-copy="catalogueDownloadText">↓ Download finish catalogue</a>';
    } else {
      html += '<a class="pdpcv-fin-disabled-link" aria-disabled="true" title="Coming soon" tabindex="-1" data-pdpcv-copy="catalogueDownloadText">↓ Download finish catalogue</a>';
    }
    html +=   '<span class="pdpcv-fin-disabled-sep" aria-hidden="true">·</span>';
    html +=   '<a class="pdpcv-fin-link" href="#" data-pdpcv-finish-upload>↑ Upload to color match</a>';
    html += '</div>';

    // v2.5 iter22: Sample-request panel — populated by renderFinishSamplesPanel(); hidden when empty.
    html += '<div class="pdpcv-fin-samples" data-pdpcv-fin-samples hidden>';
    html +=   '<div class="pdpcv-fin-samples-title">Sample Request Added:</div>';
    html +=   '<ul class="pdpcv-fin-samples-list" data-pdpcv-fin-samples-list></ul>';
    html +=   '<p class="pdpcv-fin-samples-note">You can tell us where to ship it in the next step.</p>';
    html += '</div>';

    container.innerHTML = html;
    container.setAttribute('data-pdpcv-rendered', 'true');

    // Render the samples panel from current state
    renderFinishSamplesPanel();

    // Apply copy strings (tab labels, catalogue text)
    applyCopyTo(container);

    // Wire sort dropdowns
    container.querySelectorAll('[data-pdpcv-fin-sort]').forEach(function (sel) {
      sel.addEventListener('change', applyFinishFilters);
    });

    // Round 5 iter4: "Upload to color match" — opens the file picker in-place WITHOUT
    // navigating to Step 3. The Step 3 file input handler does the actual work (adds to
    // state.step3.attachments + fires the toast). User stays on Step 1.
    // Round 5 iter8: flag so the next file upload fires the color-match toast variant.
    var finishUploadLink = container.querySelector('[data-pdpcv-finish-upload]');
    if (finishUploadLink) {
      finishUploadLink.addEventListener('click', function (e) {
        e.preventDefault();
        window.__pdpcvPendingColorMatch = true;
        var fi = document.querySelector('[data-pdpcv-file-input]');
        if (fi) fi.click();
      });
    }

    // Wire grid (event delegation)
    var gridWrap = container.querySelector('[data-pdpcv-fin-grid-wrap]');
    gridWrap.addEventListener('click', function (e) {
      // Round 5 iter8: clicking the magnifier opens the lightbox only — must not select the card.
      if (e.target.closest('.pdpcv-card-magnify')) return;
      var card = e.target.closest('[data-pdpcv-fin-card]');
      if (!card) return;
      var code = card.getAttribute('data-pdpcv-fin-card');
      var finish = (data.finishings || []).find(function (f) { return f.code === code; });
      if (finish) assignFinishRole(finish, activeFinishTab);
    });

    // Wire selected-row interactions (clear button + slot click for active-role switch / paste-to-search)
    var selRow = container.querySelector('[data-pdpcv-fin-selected-row]');
    selRow.addEventListener('click', function (e) {
      // Clear button takes priority — stops bubbling so slot click doesn't fire
      var clearBtn = e.target.closest('[data-pdpcv-fin-clear]');
      if (clearBtn) {
        e.preventDefault();
        e.stopPropagation();
        var role = clearBtn.getAttribute('data-pdpcv-fin-clear');
        if (state.step1.finishing) {
          // v2.5 iter25: if the slot currently holds a NON-default selection AND the variant has a
          // default for this role, clicking X restores the variant default (rather than emptying).
          // If the slot holds the default itself OR no default exists, clicking X empties (original
          // behavior). Compare both code AND handle since lookupFinishByHandle resolves by either.
          var clrV = getSelectedVariant();
          var defKey = role === 'primary' ? 'defaultPrimaryFinishHandle' : 'defaultAccentFinishHandle';
          var defRaw = clrV && clrV[defKey];
          var defFin = defRaw ? lookupFinishByHandle(defRaw) : null;
          var cur = state.step1.finishing[role];
          if (defFin && cur && cur.code !== defFin.code && cur.handle !== defFin.handle) {
            state.step1.finishing[role] = defFin;
          } else {
            state.step1.finishing[role] = null;
          }
        }
        refreshFinishSelectedRow();
        refreshFinishCardStates();
        saveCache();
        return;
      }
      // v2.5 iter22: slot click — if the role is ALREADY active AND has a filled finish, paste the
      // finish name into the search bar (so user can see it in the grid). Otherwise switch active role.
      var slot = e.target.closest('[data-pdpcv-fin-slot]');
      if (!slot) return;
      var slotRole = slot.getAttribute('data-pdpcv-fin-slot');
      var slotFinish = state.step1.finishing && state.step1.finishing[slotRole];
      if (activeFinishTab === slotRole && slotFinish && slotFinish.name) {
        pasteFinishNameToSearch(slotFinish.name);
        return;
      }
      switchFinishTab(slotRole);
    });
    // Keyboard: Enter/Space on slot also switches active role (or paste if already active + filled)
    selRow.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var slot = e.target.closest('[data-pdpcv-fin-slot]');
      if (!slot) return;
      e.preventDefault();
      var slotRole = slot.getAttribute('data-pdpcv-fin-slot');
      var slotFinish = state.step1.finishing && state.step1.finishing[slotRole];
      if (activeFinishTab === slotRole && slotFinish && slotFinish.name) {
        pasteFinishNameToSearch(slotFinish.name);
        return;
      }
      switchFinishTab(slotRole);
    });

    // Wire search (debounced 200ms; goes through unified filter alongside sort dropdowns)
    var search = container.querySelector('[data-pdpcv-fin-search]');
    var searchTimer = null;
    search.addEventListener('input', function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(applyFinishFilters, 200);
      updateSearchClearVisibility(search);
    });
    // v2.5 iter22: (X) clear button — clears input + re-runs filter.
    var searchClearBtn = container.querySelector('[data-pdpcv-fin-search-clear]');
    if (searchClearBtn) {
      searchClearBtn.addEventListener('click', function () {
        search.value = '';
        applyFinishFilters();
        updateSearchClearVisibility(search);
        search.focus();
      });
    }
    updateSearchClearVisibility(search);

    // v2.5 iter22: 2s hover description tip on finish cards (desktop only — `(hover: hover)` matches).
    bindFinishDescriptionTips(gridWrap);

    // v2.5 iter22: Sample-panel "X" remove buttons (event delegated).
    var samplesEl = container.querySelector('[data-pdpcv-fin-samples]');
    if (samplesEl) {
      samplesEl.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-pdpcv-fin-sample-remove]');
        if (!rm) return;
        e.preventDefault();
        removeFinishSample(rm.getAttribute('data-pdpcv-fin-sample-remove'));
      });
    }
  }

  // v2.5 iter22: paste a finish name into the search input + run the filter.
  function pasteFinishNameToSearch(name) {
    var sb = document.querySelector('[data-pdpcv-fin-search]');
    if (!sb) return;
    sb.value = name || '';
    sb.focus();
    applyFinishFilters();
    updateSearchClearVisibility(sb);
  }
  function updateSearchClearVisibility(input) {
    if (!input) return;
    var btn = input.parentElement && input.parentElement.querySelector('[data-pdpcv-fin-search-clear]');
    if (!btn) return;
    btn.hidden = !(input.value && input.value.length > 0);
  }

  // v2.5 iter23: 1.25s hover-to-show description tooltip on finishing cards. Desktop only.
  // Tip positions itself near the mouse cursor (flips to left/right/top/bottom based on viewport room
  // so it always stays within the screen). Reuses a single fixed-positioned DOM node.
  var finDescTipEl = null;
  function getFinDescTipEl() {
    if (finDescTipEl) return finDescTipEl;
    finDescTipEl = document.createElement('div');
    finDescTipEl.className = 'pdpcv-fin-desc-tip';
    document.body.appendChild(finDescTipEl);
    return finDescTipEl;
  }
  function positionFinDescTipAt(mouseX, mouseY) {
    var tip = finDescTipEl;
    if (!tip) return;
    var rect = tip.getBoundingClientRect();
    var w = rect.width, h = rect.height;
    var vw = window.innerWidth, vh = window.innerHeight;
    var margin = 12;
    var offset = 14;
    // Horizontal: try right of cursor; flip to left if overflowing right edge; clamp to viewport.
    var left = mouseX + offset;
    if (left + w > vw - margin) left = mouseX - offset - w;
    if (left < margin) left = margin;
    // Vertical: try below cursor; flip to above if overflowing bottom edge; clamp to viewport.
    var top = mouseY + offset;
    if (top + h > vh - margin) top = mouseY - offset - h;
    if (top < margin) top = margin;
    tip.style.left = left + 'px';
    tip.style.top = top + 'px';
  }
  function showFinDescTipAt(mouseX, mouseY, description) {
    var tip = getFinDescTipEl();
    tip.textContent = description;
    // Park offscreen first so we can measure dimensions, then position next to cursor.
    tip.style.left = '-9999px';
    tip.style.top = '-9999px';
    tip.classList.add('is-visible');
    positionFinDescTipAt(mouseX, mouseY);
  }
  function hideFinDescTip() {
    if (finDescTipEl) finDescTipEl.classList.remove('is-visible');
  }
  function bindFinishDescriptionTips(gridWrap) {
    if (!gridWrap) return;
    // Desktop-only — touch devices skip the hover tip entirely.
    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;
    gridWrap.querySelectorAll('[data-pdpcv-fin-card]').forEach(function (card) {
      var code = card.getAttribute('data-pdpcv-fin-card');
      var f = (data.finishings || []).find(function (x) { return x.code === code; });
      if (!f || !f.description) return; // no description → no tip
      var hoverTimer = null;
      var lastX = 0, lastY = 0;
      card.addEventListener('mouseenter', function (e) {
        lastX = e.clientX; lastY = e.clientY;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () { showFinDescTipAt(lastX, lastY, f.description); }, 1250);
      });
      card.addEventListener('mousemove', function (e) {
        lastX = e.clientX; lastY = e.clientY;
        if (finDescTipEl && finDescTipEl.classList.contains('is-visible')) {
          positionFinDescTipAt(lastX, lastY);
        }
      });
      card.addEventListener('mouseleave', function () {
        clearTimeout(hoverTimer);
        hideFinDescTip();
      });
    });
  }

  function renderSelectedSwatchSlot(role) {
    var slot = state.step1.finishing && state.step1.finishing[role];
    var isActive = activeFinishTab === role;
    // Round 5 iter13: header label sits above the slot card — distinct font weight/size from the
    // selected finish name so the role (Primary / Accent) reads as a section heading.
    var labelText = role === 'primary' ? 'Primary Finish' : 'Accent Finish (Optional)';
    var html = '<div class="pdpcv-fin-slot pdpcv-fin-slot--' + role + (slot ? ' is-filled' : '') + (isActive ? ' is-active' : '') + '" data-pdpcv-fin-slot="' + role + '" tabindex="0" role="button" aria-label="Set ' + role + ' as active finish slot">';
    html += '<div class="pdpcv-fin-slot-role">' + labelText + '</div>';
    if (slot) {
      html += '<div class="pdpcv-fin-slot-card">';
      if (slot.image) html += '<img class="pdpcv-fin-slot-img" src="' + escapeHtml(slot.image) + '" alt="" loading="lazy">';
      else html += '<div class="pdpcv-fin-slot-img-placeholder"></div>';
      html += '<div class="pdpcv-fin-slot-meta">';
      html +=   '<div class="pdpcv-fin-slot-name">' + escapeHtml(slot.name || '') + '</div>';
      if (slot.code) html += '<div class="pdpcv-fin-slot-code">' + escapeHtml(slot.code) + '</div>';
      html += '</div>';
      html += '<button type="button" class="pdpcv-fin-slot-clear" data-pdpcv-fin-clear="' + role + '" aria-label="Clear ' + role + ' finish">';
      html +=   '<svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>';
      html += '</button>';
      html += '</div>';
    } else {
      html += '<div class="pdpcv-fin-slot-empty">' + (role === 'primary' ? 'Pick a primary finish' : 'Pick an accent finish (optional)') + '</div>';
    }
    html += '</div>';
    return html;
  }

  function uniqueFinishValues(field) {
    var seen = {};
    var out = [];
    (data.finishings || []).forEach(function (f) {
      var v = (f[field] || '').trim();
      if (!v || seen[v]) return;
      seen[v] = true;
      out.push(v);
    });
    out.sort(function (a, b) { return a.localeCompare(b); });
    return out;
  }

  function applyFinishFilters() {
    var wrap = document.querySelector('[data-pdpcv-fin-grid-wrap]');
    if (!wrap) return;
    var catSel = document.querySelector('[data-pdpcv-fin-sort="category"]');
    var toneSel = document.querySelector('[data-pdpcv-fin-sort="tone"]');
    var search = document.querySelector('[data-pdpcv-fin-search]');
    var cat = catSel ? catSel.value : '';
    var tone = toneSel ? toneSel.value : '';
    var q = search ? search.value.toLowerCase().trim() : '';
    var tonesWithVisible = {};
    wrap.querySelectorAll('[data-pdpcv-fin-card]').forEach(function (c) {
      var nm = c.getAttribute('data-pdpcv-fin-name') || '';
      var tn = c.getAttribute('data-pdpcv-fin-tone') || '';
      var ct = c.getAttribute('data-pdpcv-fin-cat') || '';
      var code = (c.getAttribute('data-pdpcv-fin-card') || '').toLowerCase();
      var visible = true;
      if (cat && ct !== cat) visible = false;
      if (visible && tone && tn !== tone) visible = false;
      if (visible && q) visible = (nm.indexOf(q) >= 0 || tn.indexOf(q) >= 0 || ct.indexOf(q) >= 0 || code.indexOf(q) >= 0);
      c.hidden = !visible;
      if (visible) tonesWithVisible[tn || 'other'] = true;
    });
    wrap.querySelectorAll('[data-pdpcv-fin-tone-divider]').forEach(function (d) {
      var tn = d.getAttribute('data-pdpcv-fin-tone-divider') || 'other';
      d.hidden = !tonesWithVisible[tn];
    });
  }

  function renderFinishGrid(sorted) {
    var html = '';
    var currentTone = null;
    sorted.forEach(function (f) {
      var tone = f.tone || 'Other';
      if (tone !== currentTone) {
        html += '<div class="pdpcv-fin-tone-divider" data-pdpcv-fin-tone-divider="' + escapeHtml(tone.toLowerCase()) + '"><span>' + escapeHtml(tone) + '</span></div>';
        currentTone = tone;
      }
      html += renderFinishCard(f);
    });
    return html;
  }

  function renderFinishCard(f) {
    var primary = state.step1.finishing && state.step1.finishing.primary;
    var accent = state.step1.finishing && state.step1.finishing.accent;
    var isPrimary = primary && primary.code === f.code;
    var isAccent = accent && accent.code === f.code;
    var selClass = '';
    if (isPrimary) selClass = ' is-selected is-selected-primary';
    else if (isAccent) selClass = ' is-selected is-selected-accent';
    var html = '<button type="button" class="pdpcv-fin-card' + selClass + '"';
    html += ' data-pdpcv-fin-card="' + escapeHtml(f.code || '') + '"';
    html += ' data-pdpcv-fin-name="' + escapeHtml((f.name || '').toLowerCase()) + '"';
    html += ' data-pdpcv-fin-tone="' + escapeHtml((f.tone || '').toLowerCase()) + '"';
    html += ' data-pdpcv-fin-cat="' + escapeHtml((f.category || '').toLowerCase()) + '"';
    html += '>';
    if (f.image) {
      html += '<img class="pdpcv-fin-card-img" src="' + escapeHtml(f.image) + '" alt="" loading="lazy">';
    } else {
      html += '<div class="pdpcv-fin-card-img-placeholder"></div>';
    }
    // v2.5 iter22: magnifier always visible (CSS). Lightbox payload includes finish code + description
    // so the lightbox can show the description + the "Request as Sample" checkbox.
    if (f.image) {
      html += '<span class="pdpcv-card-magnify" data-pdpcv-lightbox-open="' + escapeHtml(f.image) + '|' + escapeHtml(f.name || '') + '"';
      html += ' data-pdpcv-finish-code="' + escapeHtml(f.code || '') + '"';
      html += ' data-pdpcv-finish-name="' + escapeHtml(f.name || '') + '"';
      html += ' data-pdpcv-finish-image="' + escapeHtml(f.image || '') + '"';
      html += ' data-pdpcv-finish-description="' + escapeHtml(f.description || '') + '"';
      html += ' role="button" tabindex="0" aria-label="View ' + escapeHtml(f.name || 'image') + ' close-up">';
      html += '<svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Zm112,0a8,8,0,0,1-8,8H120v24a8,8,0,0,1-16,0V120H80a8,8,0,0,1,0-16h24V80a8,8,0,0,1,16,0v24h24A8,8,0,0,1,152,112Z"></path></svg>';
      html += '</span>';
    }
    html += '<div class="pdpcv-fin-card-name">' + escapeHtml(f.name || '') + '</div>';
    if (f.code) html += '<div class="pdpcv-fin-card-code">' + escapeHtml(f.code) + '</div>';
    if (isPrimary) html += '<div class="pdpcv-fin-card-role-tag">Primary</div>';
    else if (isAccent) html += '<div class="pdpcv-fin-card-role-tag">Accent</div>';
    html += '</button>';
    return html;
  }

  // v2.5 iter22: Sample-request system.
  // Users can request up to 5 finish samples via a checkbox in the lightbox.
  // Samples list renders below .pdpcv-fin-footer in the finishing subform.
  var MAX_FIN_SAMPLES = 5;
  function ensureSamples() {
    if (!state.step1.finishing) state.step1.finishing = { primary: null, accent: null, samples: [] };
    if (!Array.isArray(state.step1.finishing.samples)) state.step1.finishing.samples = [];
    return state.step1.finishing.samples;
  }
  function isSampleAdded(code) {
    if (!code) return false;
    return ensureSamples().some(function (s) { return s.code === code; });
  }
  function addFinishSample(finish) {
    if (!finish || !finish.code) return false;
    var samples = ensureSamples();
    if (samples.some(function (s) { return s.code === finish.code; })) return true; // already added
    if (samples.length >= MAX_FIN_SAMPLES) {
      flashErrorToast('Max sample quantity is 5.', 'Sample request');
      return false;
    }
    samples.push({ code: finish.code, name: finish.name || '', image: finish.image || '' });
    renderFinishSamplesPanel();
    saveCache();
    return true;
  }
  function removeFinishSample(code) {
    if (!code) return;
    var samples = ensureSamples();
    var idx = samples.findIndex(function (s) { return s.code === code; });
    if (idx >= 0) {
      samples.splice(idx, 1);
      renderFinishSamplesPanel();
      saveCache();
      // v2.5 iter26: if the lightbox is currently open on this finish, uncheck its checkbox + fade
      // the note out (and clear any fail state) via .is-visible toggle (no reflow).
      var lbCheckbox = document.querySelector('[data-pdpcv-sample-checkbox]');
      var lbCode = lbCheckbox && lbCheckbox.dataset.finishCode;
      if (lbCheckbox && lbCode === code) {
        lbCheckbox.checked = false;
        var note = document.querySelector('[data-pdpcv-sample-note]');
        if (note) note.classList.remove('is-visible');
        var fail = document.querySelector('[data-pdpcv-sample-fail]');
        if (fail) fail.classList.remove('is-visible');
      }
    }
  }
  function renderFinishSamplesPanel() {
    var panel = document.querySelector('[data-pdpcv-fin-samples]');
    if (!panel) return;
    var list = panel.querySelector('[data-pdpcv-fin-samples-list]');
    var samples = ensureSamples();
    panel.hidden = samples.length === 0;
    if (!list) return;
    list.innerHTML = '';
    samples.forEach(function (s) {
      var li = document.createElement('li');
      var nameSpan = document.createElement('span');
      nameSpan.className = 'pdpcv-fin-sample-name';
      nameSpan.textContent = s.name || s.code;
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'pdpcv-fin-sample-remove';
      rm.setAttribute('data-pdpcv-fin-sample-remove', s.code);
      rm.setAttribute('aria-label', 'Remove ' + (s.name || s.code) + ' sample');
      rm.innerHTML = '<svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
      li.appendChild(nameSpan);
      li.appendChild(rm);
      list.appendChild(li);
    });
  }

  function assignFinishRole(finish, role) {
    if (!finish) return;
    if (!state.step1.finishing) state.step1.finishing = { primary: null, accent: null };
    var otherRole = (role === 'primary') ? 'accent' : 'primary';

    // Toggle off if clicking the already-selected finish in the active role
    if (state.step1.finishing[role] && state.step1.finishing[role].code === finish.code) {
      state.step1.finishing[role] = null;
    } else {
      // Mutual exclusion: if this finish currently fills the OTHER role, clear it from there
      if (state.step1.finishing[otherRole] && state.step1.finishing[otherRole].code === finish.code) {
        state.step1.finishing[otherRole] = null;
      }
      state.step1.finishing[role] = {
        code: finish.code,
        name: finish.name,
        image: finish.image || null,
        tone: finish.tone || 'Other',
        category: finish.category || ''
      };
    }
    refreshFinishSelectedRow();
    refreshFinishCardStates();
    saveCache();
  }

  function refreshFinishSelectedRow() {
    var row = document.querySelector('[data-pdpcv-fin-selected-row]');
    if (!row) return;
    row.innerHTML = renderSelectedSwatchSlot('primary') + renderSelectedSwatchSlot('accent');
    applyCopyTo(row);
  }

  function refreshFinishCardStates() {
    var primary = state.step1.finishing && state.step1.finishing.primary;
    var accent = state.step1.finishing && state.step1.finishing.accent;
    var primaryCode = primary && primary.code;
    var accentCode = accent && accent.code;
    document.querySelectorAll('[data-pdpcv-fin-card]').forEach(function (card) {
      var code = card.getAttribute('data-pdpcv-fin-card');
      var isP = code === primaryCode;
      var isA = code === accentCode;
      card.classList.toggle('is-selected-primary', isP);
      card.classList.toggle('is-selected-accent', isA);
      card.classList.toggle('is-selected', isP || isA);
      // Update or remove the role tag
      var existing = card.querySelector('.pdpcv-fin-card-role-tag');
      if (isP || isA) {
        var tag = isP ? 'Primary' : 'Accent';
        if (existing) existing.textContent = tag;
        else {
          var t = document.createElement('div');
          t.className = 'pdpcv-fin-card-role-tag';
          t.textContent = tag;
          card.appendChild(t);
        }
      } else if (existing) {
        existing.parentNode.removeChild(existing);
      }
    });
  }

  function switchFinishTab(role) {
    if (role !== 'primary' && role !== 'accent') return;
    if (activeFinishTab === role) return; // no-op
    activeFinishTab = role;
    refreshFinishSelectedRow(); // re-render slots with new is-active class
  }

  // ---------- Features sub-form ----------
  var FEATURE_LIMIT = 5;

  function renderFeatures() {
    var container = document.querySelector('[data-pdpcv-subform="features"]');
    if (!container) return;
    if (container.getAttribute('data-pdpcv-rendered') === 'true') return;

    var features = (data && data.features) || [];

    // Catalog not yet loaded: render loading state, trigger fetch, re-render when done.
    if (!features.length && !catalogLoaded) {
      container.innerHTML = '<div class="pdpcv-subform-empty">Loading features…</div>';
      loadCatalogData().then(function () { renderFeatures(); });
      return;
    }

    if (!features.length) {
      container.innerHTML = '<div class="pdpcv-subform-empty">No feature add-ons available for this product yet.</div>';
      container.setAttribute('data-pdpcv-rendered', 'true');
      return;
    }

    if (!Array.isArray(state.step1.features)) state.step1.features = [];

    // Round 5 iter8: A removed the 5-feature cap entirely — no helper line, no counter, no limit.
    var html = '';
    html += '<div class="pdpcv-feat-grid" data-pdpcv-feat-grid>';
    features.forEach(function (f) {
      html += renderFeatureCard(f);
    });
    html += '</div>';

    container.innerHTML = html;
    container.setAttribute('data-pdpcv-rendered', 'true');

    applyCopyTo(container);

    var grid = container.querySelector('[data-pdpcv-feat-grid]');
    grid.addEventListener('click', function (e) {
      // Round 5 iter8: magnifier opens lightbox only — must not toggle the feature.
      if (e.target.closest('.pdpcv-card-magnify')) return;
      var card = e.target.closest('[data-pdpcv-feat-card]');
      if (!card) return;
      var code = card.getAttribute('data-pdpcv-feat-card');
      var feature = (data.features || []).find(function (f) { return f.code === code; });
      if (feature) toggleFeature(feature);
    });

    // Round 5 iter10: mobile long-press to preview secondary image. 280ms hold, cancels on >10px move.
    // touchend or pointerup clears .is-touched so the primary image returns.
    bindFeatTouchSwap(grid);

    refreshFeatureSelectedStates();
  }

  // Round 5 iter10/12: mobile long-press to preview secondary image. 280ms hold, cancels on >10px move.
  // (iter11 had switched to instant tap; iter12 reverts per A's direction so tap-to-select stays clean.)
  function bindFeatTouchSwap(scope) {
    var HOLD_MS = 280;
    var MOVE_CANCEL_PX = 10;
    var holdTimer = null;
    var startX = 0, startY = 0;
    var activeCard = null;

    function clearHold() {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
      if (activeCard) {
        activeCard.classList.remove('is-touched');
        activeCard = null;
      }
    }

    scope.addEventListener('touchstart', function (e) {
      var card = e.target.closest('.pdpcv-feat-card.has-secondary');
      if (!card) return;
      if (e.target.closest('.pdpcv-card-magnify')) return;
      activeCard = card;
      var t = e.touches[0];
      startX = t.clientX; startY = t.clientY;
      clearTimeout(holdTimer);
      holdTimer = setTimeout(function () {
        if (activeCard) activeCard.classList.add('is-touched');
      }, HOLD_MS);
    }, { passive: true });

    scope.addEventListener('touchmove', function (e) {
      if (!activeCard) return;
      var t = e.touches[0];
      var dx = t.clientX - startX, dy = t.clientY - startY;
      if (Math.abs(dx) > MOVE_CANCEL_PX || Math.abs(dy) > MOVE_CANCEL_PX) {
        clearHold(); // user is scrolling — don't preview, don't select
      }
    }, { passive: true });

    scope.addEventListener('touchend', clearHold, { passive: true });
    scope.addEventListener('touchcancel', clearHold, { passive: true });
  }

  function renderFeatureCard(f) {
    var isSelected = state.step1.features && state.step1.features.some(function (s) { return s.code === f.code; });
    var selClass = isSelected ? ' is-selected' : '';
    // v2.5 iter20: secondaryImages is an ARRAY. First image drives hover/long-press swap; all show in lightbox.
    var secs = Array.isArray(f.secondaryImages) ? f.secondaryImages.filter(Boolean) : [];
    var hasSec = secs.length > 0;
    var secClass = hasSec ? ' has-secondary' : '';
    var html = '<button type="button" class="pdpcv-feat-card' + selClass + secClass + '" data-pdpcv-feat-card="' + escapeHtml(f.code || '') + '">';
    // Stack primary + first secondary image. Secondary opacity:0 by default,
    // CSS toggles to 1 on .pdpcv-feat-card:hover (desktop) or .is-touched (mobile long-press).
    if (f.image) {
      html += '<div class="pdpcv-feat-card-imgwrap">';
      html +=   '<img class="pdpcv-feat-card-img pdpcv-feat-card-img--primary" src="' + escapeHtml(f.image) + '" alt="" loading="lazy">';
      if (hasSec) {
        html += '<img class="pdpcv-feat-card-img pdpcv-feat-card-img--secondary" src="' + escapeHtml(secs[0]) + '" alt="" loading="lazy" aria-hidden="true">';
      }
      html += '</div>';
    } else {
      html += '<div class="pdpcv-feat-card-img-placeholder"></div>';
    }
    // Magnifier — lightbox payload includes primary + ALL secondary URLs.
    // Format: primary|caption|secondary1[|secondary2…]. Parser uses parts.slice(2) for extras.
    if (f.image) {
      var lbPayload = f.image + '|' + (f.name || '') + (hasSec ? ('|' + secs.join('|')) : '');
      html += '<span class="pdpcv-card-magnify" data-pdpcv-lightbox-open="' + escapeHtml(lbPayload) + '" role="button" tabindex="0" aria-label="View ' + escapeHtml(f.name || 'image') + ' close-up">';
      html += '<svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Zm112,0a8,8,0,0,1-8,8H120v24a8,8,0,0,1-16,0V120H80a8,8,0,0,1,0-16h24V80a8,8,0,0,1,16,0v24h24A8,8,0,0,1,152,112Z"></path></svg>';
      html += '</span>';
    }
    html += '<div class="pdpcv-feat-card-meta">';
    html +=   '<div class="pdpcv-feat-card-name">' + escapeHtml(f.name || '') + '</div>';
    if (f.code) html += '<div class="pdpcv-feat-card-code">' + escapeHtml(f.code) + '</div>';
    html += '</div>';
    html += '</button>';
    return html;
  }

  // Round 5 iter8: feature cap removed — unlimited selection.
  function toggleFeature(feature) {
    if (!feature) return;
    if (!Array.isArray(state.step1.features)) state.step1.features = [];
    var idx = state.step1.features.findIndex(function (s) { return s.code === feature.code; });
    if (idx >= 0) {
      state.step1.features.splice(idx, 1);
    } else {
      state.step1.features.push({ code: feature.code, name: feature.name });
    }
    refreshFeatureSelectedStates();
    saveCache();
  }

  function refreshFeatureSelectedStates() {
    var selectedCodes = (state.step1.features || []).map(function (s) { return s.code; });
    document.querySelectorAll('[data-pdpcv-feat-card]').forEach(function (card) {
      var code = card.getAttribute('data-pdpcv-feat-card');
      card.classList.toggle('is-selected', selectedCodes.indexOf(code) >= 0);
    });
  }

  function showLimitToast() {
    var el = document.querySelector('[data-pdpcv-draft-saved]');
    if (!el) return;
    var span = el.querySelector('span[data-pdpcv-copy]') || el.querySelector('span');
    if (!span) return;
    var origText = (data.copy && data.copy.draftSaved) || 'Draft saved ✓';
    span.textContent = 'Maximum ' + FEATURE_LIMIT + ' features selected';
    el.hidden = false;
    el.setAttribute('data-pdpcv-visible', '');
    el.classList.add('is-limit-toast');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.removeAttribute('data-pdpcv-visible');
      setTimeout(function () {
        el.hidden = true;
        el.classList.remove('is-limit-toast');
        span.textContent = origText;
      }, FADE_MS);
    }, 2000);
  }

  // Helper: re-apply data.copy strings to a freshly-rendered scope
  function applyCopyTo(scope) {
    var c = (data && data.copy) || {};
    scope.querySelectorAll('[data-pdpcv-copy]').forEach(function (el) {
      var k = el.getAttribute('data-pdpcv-copy');
      if (c[k] !== undefined && c[k] !== null) {
        el.textContent = c[k];
      }
    });
  }

  // ---------- Step 2 validation ----------
  function validateStep2() {
    var errs = [];
    document.querySelectorAll('[data-pdpcv-validation]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });

    var requiredMsg = (data.copy && data.copy.validationRequired) || 'Required';
    var invalidEmailMsg = (data.copy && data.copy.validationInvalidEmail) || 'Please enter a valid email';

    var name = (state.step2.name || '').trim();
    if (!name) { showFieldError('name', requiredMsg); errs.push('name'); }

    var email = (state.step2.email || '').trim();
    if (!email) {
      showFieldError('email', requiredMsg); errs.push('email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showFieldError('email', invalidEmailMsg); errs.push('email');
    }

    if (state.step2.preferredContact === 'phone') {
      var phone = (state.step2.phone || '').trim();
      if (!phone) { showFieldError('phone', requiredMsg); errs.push('phone'); }
    }

    // v2.5 iter39: address required when 1+ samples requested in Step 1. address2 (apartment) optional.
    var samples = (state.step1.finishing && state.step1.finishing.samples) || [];
    if (samples.length > 0) {
      ['address1', 'city', 'state', 'zip'].forEach(function (f) {
        var v = (state.step2[f] || '').trim();
        if (!v) { showFieldError(f, requiredMsg); errs.push(f); }
      });
    }

    if (errs.length) {
      var firstField = document.querySelector('[data-pdpcv-input="' + errs[0] + '"]');
      if (firstField && typeof firstField.focus === 'function') firstField.focus();
      return false;
    }
    return true;
  }

  function showFieldError(field, msg) {
    var el = document.querySelector('[data-pdpcv-validation="' + field + '"]');
    if (el) {
      el.textContent = msg;
      el.hidden = false;
    }
  }

  function clearFieldError(field) {
    var el = document.querySelector('[data-pdpcv-validation="' + field + '"]');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  // ---------- Submit (Phase 4 — /contact AJAX) ----------
  function submitForm() {
    // Re-validate Step 2 defensively (user could have jumped here via indicator click)
    if (!validateStep2()) {
      setStep(2);
      return;
    }

    var submitBtn = document.querySelector('[data-pdpcv-submit]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-submitting');
    }
    clearSubmitError();

    // v2.5 (2026-05-13): make sure IDB-stored files are hydrated into attachmentFiles
    // before we build the multipart body. If hydration is already complete this resolves
    // synchronously; otherwise we wait so we don't ship the form before files arrive.
    hydrateAttachmentFilesFromIdb().then(performSubmit);
  }

  function performSubmit() {
    var submitBtn = document.querySelector('[data-pdpcv-submit]');
    var fd = buildContactFormData();
    var webhookPayload = buildWebhookPayload();

    // Round 5 iter7: log payload for failed-email investigation. Strip when emails reliably arrive.
    if (window.console && console.log) {
      var dump = {};
      fd.forEach(function (v, k) { dump[k] = v; });
      console.log('[PDPCV] submit payload', dump);
      console.log('[PDPCV] webhook payload', webhookPayload);
    }

    // v2.5 (2026-05-13): submit in parallel to /contact (Shopify email) and the
    // arc-custom-requests Fly.io webhook (writes structured Airtable record).
    // Promise.allSettled — neither path blocks the other; Shopify drives UI.
    var shopifyFetch = fetch('/contact', {
      method: 'POST', body: fd, redirect: 'follow', credentials: 'same-origin'
    });

    // v2.5 (2026-05-13): when files are picked, switch to multipart so the
    // webhook can stream them to Airtable's content-upload endpoint. The
    // server accepts either content-type; pure-JSON path stays available for
    // attachment-less submissions (smaller request body).
    // Filter null entries — they exist when an attachment's IDB blob couldn't be
    // rehydrated (old cache pre-IDB, IDB disabled, etc.). Don't attach them.
    var realFiles = (attachmentFiles || []).filter(function (f) { return f != null; });
    var webhookFetch;
    if (realFiles.length > 0) {
      var wfd = new FormData();
      wfd.append('payload', JSON.stringify(webhookPayload));
      realFiles.forEach(function (file, idx) {
        wfd.append('attachment_' + idx, file, file.name);
      });
      webhookFetch = fetch(CUSTOM_REQUEST_WEBHOOK_URL, { method: 'POST', body: wfd });
    } else {
      webhookFetch = fetch(CUSTOM_REQUEST_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(webhookPayload)
      });
    }

    Promise.allSettled([shopifyFetch, webhookFetch]).then(function (results) {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-submitting');
      }
      var shopifyResult = results[0];
      var webhookResult = results[1];

      // Log webhook outcome but never surface it to the user — Airtable write is best-effort.
      if (window.console) {
        if (webhookResult.status === 'fulfilled') {
          console.log('[PDPCV] webhook status', webhookResult.value.status);
        } else {
          console.warn('[PDPCV] webhook failed:', webhookResult.reason);
        }
      }

      // Shopify result drives the user-visible UI (the email is the customer-facing artifact).
      if (shopifyResult.status === 'fulfilled') {
        var res = shopifyResult.value;
        // 2026-09-16 (round 122 W5): `status < 500` counted every 4xx as a
        // success, so a rejected submission showed the confirmation step and
        // the inquiry was lost in silence. A followed redirect ends at a 2xx,
        // so `ok` or `redirected` is the real test.
        if (res.ok || res.redirected) {
          clearCache();
          setStep(4);
        } else {
          showSubmitError('Submission failed (status ' + res.status + '). Please try again.');
        }
      } else {
        showSubmitError('Network error. Please check your connection and try again.');
        if (window.console) console.error('[PDPCV] Submit failed:', shopifyResult.reason);
      }
    });
  }

  // v2.5 (2026-05-13): build the JSON payload sent to the Fly.io webhook.
  // Mirrors the Shopify FormData fields but in JSON shape (server expects this contract).
  function buildWebhookPayload() {
    var ctx = collectSubmissionContext();
    return {
      contact: {
        name: ctx.name,
        email: ctx.email,
        phone: ctx.phone,
        preferredContact: ctx.preferredContact
      },
      shipping: { addressFormatted: ctx.shippingAddress },
      product: ctx.product || { title: '', handle: '', url: '', type: '' },
      variant: ctx.variant || { title: '', id: null },
      dimensions: {
        unit: ctx.dimUnit,
        primary: ctx.dimPrimary || '',
        secondary: ctx.dimSecondary || ''
      },
      finishings: {
        selected: ctx.finishingSelected,
        samples: ctx.finishingSamples
      },
      features: ctx.featuresList,
      featuresDetailed: ctx.featuresDetailed || [],
      notes: ctx.notesClean,
      timing: ctx.timing
    };
  }

  // v2.5 (2026-05-13): collect "view model" once so both the Shopify /contact POST
  // and the Fly.io webhook payload derive from the same source. Renamed Shopify
  // FormData field keys per A's spec; address fields combined into one string.
  function collectSubmissionContext() {
    var ctx = {
      name: (state.step2.name || '').trim(),
      email: (state.step2.email || '').trim(),
      phone: (state.step2.phone || '').trim(),
      preferredContact: state.step2.preferredContact || 'email',
      shippingAddress: '',
      product: null,
      variant: null,
      dimUnit: 'in',
      dimPrimary: '',
      dimSecondary: '',
      finishingSelected: '',
      finishingSamples: '',
      featuresList: '',
      featuresDetailed: [],
      notesClean: '',
      timing: {
        draftStartedAt: state.firstSavedAt || null,
        submittedAt: Date.now()
      }
    };

    // Shipping address — only when samples were requested. Combine into one string,
    // skipping empty parts so a missing apt doesn't leave ", , " gaps.
    var sampleListForAddress = (state.step1.finishing && state.step1.finishing.samples) || [];
    if (sampleListForAddress.length > 0) {
      var parts = [
        (state.step2.address1 || '').trim(),
        (state.step2.address2 || '').trim(),
        (state.step2.city || '').trim(),
        (state.step2.state || '').trim(),
        (state.step2.zip || '').trim()
      ].filter(function (s) { return s.length > 0; });
      ctx.shippingAddress = parts.join(', ');
    }

    if (data.product) {
      ctx.product = {
        title: data.product.title || '',
        handle: data.product.handle || '',
        url: data.product.url ? ((window.location.origin || '') + data.product.url) : '',
        type: data.product.productType || ''
      };
      var sv = getSelectedVariant();
      if (sv) {
        ctx.variant = { id: sv.id, title: sv.title || '' };
      }
    }

    // Dimensions formatted as "72in W × 30in D × 30in H" per axis (A's pick).
    // v2.5 (2026-05-13): prepend axis name as first line ("Reception Desk Size\n…").
    // The confirmation-email composer splits on the first newline to derive the
    // section heading ("Requested Reception Desk Size") — embedding the label
    // here keeps the Airtable schema lean (no separate label columns).
    var dims = state.step1.dimensions;
    if (dims && (dims.primary || dims.secondary)) {
      ctx.dimUnit = dims.unit || 'in';
      ['primary', 'secondary'].forEach(function (role) {
        var axis = dims[role];
        if (!axis) return;
        var axisName = (data.product && data.product[role + 'AxisName']) || '';
        var key = 'dim' + (role === 'primary' ? 'Primary' : 'Secondary');
        if (role === 'secondary' && dims.secondaryOptedOut) {
          ctx[key] = (axisName ? axisName + '\n' : '') + 'Opted out';
          return;
        }
        var pieces = [];
        if (axis.widthMm  != null) pieces.push(formatMmAs(axis.widthMm,  ctx.dimUnit) + ' W');
        if (axis.depthMm  != null) pieces.push(formatMmAs(axis.depthMm,  ctx.dimUnit) + ' D');
        if (axis.heightMm != null) pieces.push(formatMmAs(axis.heightMm, ctx.dimUnit) + ' H');
        var values = pieces.join(' × ');
        ctx[key] = axisName ? (axisName + '\n' + values) : values;
      });
    }

    // Finishing summary: "Primary: Midnight Black (9028) / Accent: Matte Gray (9176)"
    var fin = state.step1.finishing;
    if (fin) {
      var finPieces = [];
      if (fin.primary) {
        finPieces.push('Primary: ' + (fin.primary.name || '') + (fin.primary.code ? ' (' + fin.primary.code + ')' : ''));
      }
      if (fin.accent) {
        finPieces.push('Accent: ' + (fin.accent.name || '') + (fin.accent.code ? ' (' + fin.accent.code + ')' : ''));
      }
      ctx.finishingSelected = finPieces.join(' / ');

      // Samples: "Midnight Black (9028), Dark Oak (8084)"
      if (Array.isArray(fin.samples) && fin.samples.length) {
        ctx.finishingSamples = fin.samples.map(function (s) {
          return (s.name || '') + (s.code ? ' (' + s.code + ')' : '');
        }).join(', ');
      }
    }

    if (state.step1.features && state.step1.features.length) {
      ctx.featuresList = state.step1.features.map(function (f) { return f.name; }).join(', ');
      // v2.5 (2026-06-03): enrich with SKU + image for Airtable triage columns
      // (Selected Feature SKUs + Selected Feature Photos). Catalog lookup keeps
      // state.step1.features storage shape unchanged (draft-cache stays valid).
      var catalog = (data && data.features) || [];
      ctx.featuresDetailed = state.step1.features.map(function (f) {
        var hit = null;
        for (var i = 0; i < catalog.length; i++) {
          if (catalog[i].code === f.code) { hit = catalog[i]; break; }
        }
        return {
          name: f.name || '',
          sku: f.code || '',
          image: (hit && hit.image) || ''
        };
      });
    }

    // Extras (free-text) — sanitize + cap; ensure non-empty so Shopify accepts the email.
    var rawExtras = (state.step3.extras || '');
    var cleanExtras = rawExtras
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    if (cleanExtras.length > 5000) {
      cleanExtras = cleanExtras.slice(0, 5000) + '… [truncated]';
    }
    if (!cleanExtras.trim()) {
      var summary = [];
      if (ctx.product && ctx.product.title) summary.push('Product: ' + ctx.product.title);
      if (ctx.variant && ctx.variant.title) summary.push('Variant: ' + ctx.variant.title);
      summary.push('Customization request submitted via PDP modal.');
      cleanExtras = summary.join('\n');
    }
    ctx.notesClean = cleanExtras;
    return ctx;
  }

  function buildContactFormData() {
    var ctx = collectSubmissionContext();
    var fd = new FormData();
    fd.set('form_type', 'contact');
    fd.set('utf8', '✓');

    // Lowercase canonical keys — Shopify routes name/email/phone to the sender identity.
    fd.set('contact[name]', ctx.name);
    fd.set('contact[email]', ctx.email);
    if (ctx.phone) fd.set('contact[phone]', ctx.phone);
    fd.set('contact[Preferred Contact]', ctx.preferredContact);

    // v2.5 (2026-05-13): combined single-string shipping address (was 5 fields).
    if (ctx.shippingAddress) fd.set('contact[Shipping Address]', ctx.shippingAddress);

    if (ctx.product) {
      if (ctx.product.title)  fd.set('contact[Product Request Submitted On]', ctx.product.title); // renamed from Product
      if (ctx.product.handle) fd.set('contact[Product Handle]', ctx.product.handle);
      if (ctx.product.url)    fd.set('contact[Product URL]', ctx.product.url);
    }
    // Variant ID dropped per A's spec. Variant Title renamed to Initial Variation.
    if (ctx.variant && ctx.variant.title) fd.set('contact[Initial Variation]', ctx.variant.title);

    // Dimensions per axis — preserves the per-axis labels A uses in email triage.
    var dims = state.step1.dimensions;
    if (dims && (dims.primary || dims.secondary)) {
      var unit = ctx.dimUnit;
      ['primary', 'secondary'].forEach(function (role) {
        var axis = dims[role];
        if (!axis) return;
        var axisName = data.product[role + 'AxisName'];
        if (!axisName) return;
        if (role === 'secondary' && dims.secondaryOptedOut) {
          fd.set('contact[' + axisName + ']', 'Opted out');
          return;
        }
        if (axis.widthMm  != null) fd.set('contact[' + axisName + ' - Width]',  formatMmAs(axis.widthMm,  unit));
        if (axis.depthMm  != null) fd.set('contact[' + axisName + ' - Depth]',  formatMmAs(axis.depthMm,  unit));
        if (axis.heightMm != null) fd.set('contact[' + axisName + ' - Height]', formatMmAs(axis.heightMm, unit));
      });
    }

    var fin = state.step1.finishing;
    if (fin) {
      if (fin.primary) fd.set('contact[Primary Finish]', (fin.primary.name || '') + (fin.primary.code ? ' (' + fin.primary.code + ')' : ''));
      if (fin.accent)  fd.set('contact[Accent Finish]',  (fin.accent.name  || '') + (fin.accent.code  ? ' (' + fin.accent.code  + ')' : ''));
    }
    if (ctx.finishingSamples) fd.set('contact[Requested Finish Samples]', ctx.finishingSamples);

    if (ctx.featuresList) fd.set('contact[Requested Features]', ctx.featuresList); // renamed from Features

    // contact[body] is the canonical Shopify field — keep ONLY this one.
    // v2.5 fix (2026-05-13): A reported team Shopify notifications stopped arriving
    // after I added a duplicate `contact[Additional Notes from Client]` carrying the
    // same content. Duplicate-content custom attributes can trigger Shopify's spam
    // filter (the existing iter7 comment above warned about silent drops). Reverting
    // to body-only — readability of the field name is now provided by the new
    // Airtable confirmation email path, not the Shopify notification.
    fd.set('contact[body]', ctx.notesClean);

    // 2026-09-16 (round 122 W5): form-source attribution. `ag-form-source.js`
    // is rendered on every page from layout/theme.liquid; this FormData is
    // hand-built, so a DOM-only injector would miss it entirely.
    try {
      var agfs = window.AGFormSource;
      if (agfs && typeof agfs.fields === 'function') {
        var agfsFields = agfs.fields('customization-modal', fd); // SPEC 136: fd returns contact[body] wrapped at 72
        Object.keys(agfsFields).forEach(function (k) { fd.set(k, agfsFields[k]); });
      }
    } catch (e) { /* attribution is never allowed to stop a submission */ }

    return fd;
  }

  function showSubmitError(msg) {
    var el = document.querySelector('[data-pdpcv-submit-error]');
    if (!el) {
      var footer = document.querySelector('.pdpcv-footer');
      if (!footer) return;
      el = document.createElement('div');
      el.className = 'pdpcv-submit-error';
      el.setAttribute('data-pdpcv-submit-error', '');
      footer.parentNode.insertBefore(el, footer);
    }
    el.textContent = msg;
    el.hidden = false;
  }

  function clearSubmitError() {
    var el = document.querySelector('[data-pdpcv-submit-error]');
    if (el) { el.hidden = true; el.textContent = ''; }
  }

  // ---------- Wiring ----------

  // Close handlers (X + Close button in Step 4)
  document.querySelectorAll('[data-pdpcv-close]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      closeModal();
    });
  });

  // Backdrop click
  var backdrop = root.querySelector('[data-pdpcv-backdrop]');
  if (backdrop) {
    backdrop.addEventListener('click', closeModal);
  }

  // Escape: layered. Modal first; second Esc closes sidebar.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (isOpen()) {
        closeModal();
      } else if (isSidebarOpen()) {
        closeSidebar();
      }
    }
  });

  // Indicator click navigation
  document.querySelectorAll('[data-pdpcv-step-link]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      var step = parseInt(el.getAttribute('data-pdpcv-step-link'), 10);
      if (!isNaN(step)) setStep(step);
    });
  });

  // Footer buttons
  var backBtn = document.querySelector('[data-pdpcv-back]');
  if (backBtn) backBtn.addEventListener('click', function () { setStep(Math.max(1, state.currentStep - 1)); });
  var nextBtn = document.querySelector('[data-pdpcv-next]');
  if (nextBtn) nextBtn.addEventListener('click', function () { setStep(Math.min(3, state.currentStep + 1)); });
  var submitBtn = document.querySelector('[data-pdpcv-submit]');
  if (submitBtn) submitBtn.addEventListener('click', submitForm);

  // Round 5 iter9: animate subform height between 0 and the measured scrollHeight.
  // Replaces the older `max-height: 8000px` trick which made the easing curve apply over
  // 8000px regardless of real content size — leading to jumpy opens + delayed closes.
  function setSubformOpen(subform, open) {
    if (!subform) return;
    if (open) {
      // Apply .is-open first so padding/margin are present when we measure scrollHeight.
      subform.classList.add('is-open');
      // Snap to 0 first if not already (handles re-open mid-animation cleanly).
      var startH = subform.style.maxHeight;
      if (startH === '' || startH === 'none') subform.style.maxHeight = '0px';
      // Force reflow so the next style change creates a transition origin.
      void subform.offsetHeight;
      var target = subform.scrollHeight;
      subform.style.maxHeight = target + 'px';
      // After the transition completes, release the height so dynamic content can grow freely.
      var onEnd = function (e) {
        if (e.propertyName !== 'max-height') return;
        subform.removeEventListener('transitionend', onEnd);
        if (subform.classList.contains('is-open')) {
          subform.style.maxHeight = 'none';
        }
      };
      subform.addEventListener('transitionend', onEnd);
    } else {
      // Closing: pin current height first so the transition has a defined start, then animate to 0.
      var current = subform.scrollHeight;
      subform.style.maxHeight = current + 'px';
      void subform.offsetHeight;
      subform.classList.remove('is-open');
      subform.style.maxHeight = '0px';
    }
  }

  // Step 1 sub-form toggles (checkbox = inclusion flag, governs subform open state alongside collapse)
  document.querySelectorAll('[data-pdpcv-toggle]').forEach(function (input) {
    input.addEventListener('change', function () {
      var key = input.getAttribute('data-pdpcv-toggle');
      state.step1[key + 'Checked'] = input.checked;
      // v2.5 iter37: Collapsed mirrors !checked. Check → open (Collapsed=false, chevron down,
      // aria-expanded=true). Uncheck → collapse (Collapsed=true, chevron right, aria-expanded=false).
      state.step1[key + 'Collapsed'] = !input.checked;
      var section = document.querySelector('[data-pdpcv-section="' + key + '"]');
      if (section) section.classList.toggle('is-collapsed', !!state.step1[key + 'Collapsed']);
      var collapseBtn = document.querySelector('[data-pdpcv-collapse="' + key + '"]');
      if (collapseBtn) collapseBtn.setAttribute('aria-expanded', String(!state.step1[key + 'Collapsed']));
      if (input.checked) {
        if (key === 'dimensions') renderDimensions();
        else if (key === 'finishing') renderFinishing();
        else if (key === 'features') renderFeatures();
      }
      // Open/close after render so scrollHeight measures the real populated content.
      var subform = document.querySelector('[data-pdpcv-subform="' + key + '"]');
      setSubformOpen(subform, input.checked && !state.step1[key + 'Collapsed']);
      saveCache();
      if (state && state.currentStep) {
        requestAnimationFrame(function () { measureAndSetStepHeight(state.currentStep); });
      }
    });
  });

  // Collapse buttons: toggle subform visibility.
  // Round 5 iter8: when section is unchecked, the chevron also checks + opens (per A's rules).
  document.querySelectorAll('[data-pdpcv-collapse]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var key = btn.getAttribute('data-pdpcv-collapse');
      if (!state.step1[key + 'Checked']) {
        // Unchecked → check, which opens the subform via the existing change handler.
        var input = document.querySelector('[data-pdpcv-toggle="' + key + '"]');
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      var nowCollapsed = !state.step1[key + 'Collapsed'];
      state.step1[key + 'Collapsed'] = nowCollapsed;
      var subform = document.querySelector('[data-pdpcv-subform="' + key + '"]');
      setSubformOpen(subform, !nowCollapsed);
      var section = document.querySelector('[data-pdpcv-section="' + key + '"]');
      if (section) section.classList.toggle('is-collapsed', nowCollapsed);
      btn.setAttribute('aria-expanded', String(!nowCollapsed));
      saveCache();
      if (state && state.currentStep) {
        requestAnimationFrame(function () { measureAndSetStepHeight(state.currentStep); });
      }
    });
  });

  // v2.5 iter35: Step 1 Questions? accordion. Reuses setSubformOpen() so the panel
  // height animates the same way as the dimensions/finishing/features subforms.
  // Session-local UI affordance — no state persistence, no copy bag entry.
  var questionsTrigger = document.querySelector('[data-pdpcv-questions-trigger]');
  var questionsPanel = document.querySelector('[data-pdpcv-questions-panel]');
  if (questionsTrigger && questionsPanel) {
    questionsTrigger.addEventListener('click', function (e) {
      e.preventDefault();
      var nextOpen = questionsTrigger.getAttribute('aria-expanded') !== 'true';
      questionsTrigger.setAttribute('aria-expanded', String(nextOpen));
      setSubformOpen(questionsPanel, nextOpen);
      if (state && state.currentStep) {
        requestAnimationFrame(function () { measureAndSetStepHeight(state.currentStep); });
      }
    });
  }

  // Round 5 iter8: section-header click rules per A —
  //   - collapsed + unchecked → check AND open (no matter where in header you click, except info)
  //   - open                  → collapse (no uncheck)
  //   - checked + collapsed   → open
  //   - ONLY .pdpcv-check-box (the visual checkmark square) can toggle the checked state.
  // To enforce that, we block the native <label>→checkbox link for clicks outside the box itself.
  document.querySelectorAll('.pdpcv-check').forEach(function (label) {
    label.addEventListener('click', function (e) {
      // Allow native toggle when the user clicked the visual check-box or the hidden input directly.
      if (e.target.closest('.pdpcv-check-box')) return;
      if (e.target.matches && e.target.matches('input[type="checkbox"]')) return;
      // Anywhere else in the label (the text "Dimensions" etc): suppress the native checkbox toggle.
      e.preventDefault();
    });
  });

  document.querySelectorAll('.pdpcv-section-header').forEach(function (header) {
    header.addEventListener('click', function (e) {
      // Native check-box toggles itself; the input change handler picks it up.
      if (e.target.closest('.pdpcv-check-box')) return;
      if (e.target.matches && e.target.matches('input[type="checkbox"]')) return;
      // Info icon owns its own tooltip — don't toggle the section.
      if (e.target.closest('.pdpcv-info-wrap')) return;
      // Don't double-fire when the user clicked the chevron itself (it has its own handler).
      if (e.target.closest('.pdpcv-section-collapse')) return;

      var section = header.closest('[data-pdpcv-section]');
      if (!section) return;
      var key = section.getAttribute('data-pdpcv-section');
      var isChecked = !!state.step1[key + 'Checked'];

      if (!isChecked) {
        // Collapsed + unchecked → check, which opens the subform via the existing change handler.
        var input = section.querySelector('[data-pdpcv-toggle]');
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // Checked: header click toggles collapse-only (collapseBtn handler does this, no uncheck).
        var collapseBtn = header.querySelector('[data-pdpcv-collapse]');
        if (collapseBtn) collapseBtn.click();
      }
    });
  });

  // C4 Round 5: bind (i) info-tooltip buttons
  initInfoTooltips();

  // v2.5 iter25: per-product-type placeholder for the extras textarea (Step 1 + Step 3 instances).
  // Copy resolved server-side via Liquid pt_bucket case-switch in customization-modal-data.liquid.
  if (data && data.copy && data.copy.extrasPlaceholder) {
    document.querySelectorAll('[data-pdpcv-input="extras"]').forEach(function (el) {
      el.placeholder = data.copy.extrasPlaceholder;
    });
  }

  // Step 2 inputs (+ extras, which now has two instances — Step 1 + Step 3 — bound in parallel)
  // v2.5 iter39: address fields bind via the same pattern. State binding is unconditional;
  // visibility (whether the user can interact with them) is governed by refreshAddressVisibility().
  ['name', 'email', 'phone', 'address1', 'address2', 'city', 'state', 'zip'].forEach(function (field) {
    var input = document.querySelector('[data-pdpcv-input="' + field + '"]');
    if (!input) return;
    input.addEventListener('input', function () {
      state.step2[field] = input.value;
      clearFieldError(field);
      saveCache();
    });
  });
  // Round 5 iter5: extras has TWO inputs (Step 1 + Step 3 summary) — bind both with two-way sync.
  document.querySelectorAll('[data-pdpcv-input="extras"]').forEach(function (input) {
    input.addEventListener('input', function () {
      state.step3.extras = input.value;
      // Mirror the value to other extras inputs so the two stay in sync without flicker
      document.querySelectorAll('[data-pdpcv-input="extras"]').forEach(function (other) {
        if (other !== input && other.value !== input.value) other.value = input.value;
      });
      saveCache();
    });
  });

  // Preferred contact select (Round 5 iter3: was pills, now <select>)
  var prefSelectEl = document.querySelector('[data-pdpcv-pref-select]');
  if (prefSelectEl) {
    prefSelectEl.addEventListener('change', function () {
      state.step2.preferredContact = prefSelectEl.value;
      togglePhoneField();
      saveCache();
    });
  }

  // Round 5 iter5: lightbox for finishing + feature card magnifiers. Delegated click
  // so it works across dynamic re-renders of the grids.
  // Round 5 iter10: multi-image gallery. Data payload format: primary|caption|secondary[|tertiary…].
  // Round 5 iter11: slide animation via translateX on a flex track of all images.
  (function initLightbox() {
    var lb = document.querySelector('[data-pdpcv-lightbox]');
    if (!lb) return;
    var capEl = lb.querySelector('[data-pdpcv-lightbox-caption]');
    var thumbsEl = lb.querySelector('[data-pdpcv-lightbox-thumbs]');
    var dotsEl = lb.querySelector('[data-pdpcv-lightbox-dots]');
    var stageEl = lb.querySelector('[data-pdpcv-lightbox-stage]');
    var trackEl = lb.querySelector('[data-pdpcv-lightbox-track]');
    // v2.5 iter32: image counter "X / Y" pill — top-left of stage, only shown for multi-image lightboxes.
    var counterEl = lb.querySelector('[data-pdpcv-lightbox-counter]');
    var images = [];
    var activeIdx = 0;

    function renderThumbsAndDots() {
      var multi = images.length > 1;
      lb.classList.toggle('is-multi', multi);
      // v2.5 iter32: counter visibility tracks multi flag (CSS shows arrows via .is-multi too).
      if (counterEl) counterEl.hidden = !multi;
      if (thumbsEl) {
        thumbsEl.innerHTML = '';
        if (multi) {
          images.forEach(function (src, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'pdpcv-lightbox-thumb' + (i === activeIdx ? ' is-active' : '');
            b.setAttribute('data-pdpcv-lightbox-thumb', String(i));
            b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + images.length);
            b.innerHTML = '<img src="' + src + '" alt="">';
            thumbsEl.appendChild(b);
          });
        }
      }
      if (dotsEl) {
        dotsEl.innerHTML = '';
        if (multi) {
          images.forEach(function (_, i) {
            var d = document.createElement('button');
            d.type = 'button';
            d.className = 'pdpcv-lightbox-dot' + (i === activeIdx ? ' is-active' : '');
            d.setAttribute('data-pdpcv-lightbox-dot', String(i));
            d.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + images.length);
            dotsEl.appendChild(d);
          });
        }
      }
    }
    function setActive(i, withAnimation) {
      if (!images.length) return;
      activeIdx = ((i % images.length) + images.length) % images.length;
      if (trackEl) {
        // Allow disabling the transition for the initial open (instant position).
        trackEl.style.transition = withAnimation === false ? 'none' : '';
        trackEl.style.transform = 'translateX(' + (-activeIdx * 100) + '%)';
      }
      // Refresh selected indicators
      if (thumbsEl) Array.prototype.forEach.call(thumbsEl.querySelectorAll('.pdpcv-lightbox-thumb'), function (t, ti) {
        t.classList.toggle('is-active', ti === activeIdx);
      });
      if (dotsEl) Array.prototype.forEach.call(dotsEl.querySelectorAll('.pdpcv-lightbox-dot'), function (d, di) {
        d.classList.toggle('is-active', di === activeIdx);
      });
      // v2.5 iter32: update counter pill text.
      if (counterEl && images.length > 1) counterEl.textContent = (activeIdx + 1) + ' / ' + images.length;
    }
    function buildTrack() {
      if (!trackEl) return;
      trackEl.innerHTML = '';
      images.forEach(function (src) {
        var img = document.createElement('img');
        img.className = 'pdpcv-lightbox-img';
        img.alt = '';
        img.src = src;
        trackEl.appendChild(img);
      });
    }
    function open(imgs, caption, ctx) {
      images = imgs.filter(Boolean);
      activeIdx = 0;
      if (capEl) capEl.textContent = caption || '';

      // v2.5 iter22: finish context — show description + "Request as Sample" checkbox when present.
      var descEl = lb.querySelector('[data-pdpcv-lightbox-description]');
      var dividerEl = lb.querySelector('[data-pdpcv-lightbox-divider]');
      var sampleToggle = lb.querySelector('[data-pdpcv-sample-toggle]');
      var sampleCheckbox = lb.querySelector('[data-pdpcv-sample-checkbox]');
      var sampleNote = lb.querySelector('[data-pdpcv-sample-note]');
      var hasFinishCtx = !!(ctx && ctx.finishCode);
      var hasDescription = !!(ctx && ctx.finishDescription);
      // v2.5 iter27: branch frame styling between finish (locked 540×663 desktop, with meta block
      // + sample-state slot) and feature (auto-height, no sample slot reserved). CSS reads the class.
      lb.classList.toggle('is-finish-context', hasFinishCtx);
      if (descEl) {
        if (hasDescription) { descEl.textContent = ctx.finishDescription; descEl.hidden = false; }
        else { descEl.textContent = ''; descEl.hidden = true; }
      }
      if (dividerEl) dividerEl.hidden = !hasFinishCtx;
      if (sampleToggle) sampleToggle.hidden = !hasFinishCtx;
      if (sampleCheckbox) {
        sampleCheckbox.dataset.finishCode = (ctx && ctx.finishCode) || '';
        sampleCheckbox.dataset.finishName = (ctx && ctx.finishName) || '';
        sampleCheckbox.dataset.finishImage = (ctx && ctx.finishImage) || '';
        sampleCheckbox.checked = hasFinishCtx && isSampleAdded(ctx.finishCode);
      }
      // v2.5 iter26: pre-placed sample-note + sample-fail share a state slot; toggle .is-visible
      // (opacity transition) instead of [hidden] (display:none) so the meta block doesn't reflow
      // on click. open() syncs initial state to whether the current finish was already added.
      if (sampleNote) sampleNote.classList.toggle('is-visible', !!(sampleCheckbox && sampleCheckbox.checked));
      var sampleFail = lb.querySelector('[data-pdpcv-sample-fail]');
      if (sampleFail) sampleFail.classList.remove('is-visible');

      renderThumbsAndDots();
      buildTrack();
      setActive(0, false); // instant — no slide from previous state
      lb.hidden = false;
      requestAnimationFrame(function () { lb.classList.add('is-open'); });
    }
    function close() {
      lb.classList.remove('is-open');
      setTimeout(function () {
        lb.hidden = true;
        if (trackEl) trackEl.innerHTML = '';
        images = [];
        // v2.5 iter26: reset both sample-note and sample-fail .is-visible state on close.
        var noteEl = lb.querySelector('[data-pdpcv-sample-note]');
        if (noteEl) noteEl.classList.remove('is-visible');
        var failEl = lb.querySelector('[data-pdpcv-sample-fail]');
        if (failEl) failEl.classList.remove('is-visible');
      }, FADE_MS);
    }
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-pdpcv-lightbox-open]');
      if (opener) {
        e.preventDefault();
        e.stopPropagation();
        var raw = opener.getAttribute('data-pdpcv-lightbox-open');
        var parts = raw.split('|');
        // payload: primary|caption|secondary[|tertiary...]
        var primary = parts[0] || '';
        var caption = parts[1] || '';
        var extras = parts.slice(2);
        // v2.5 iter22: finish context (description + sample toggle) when opener is a finish-card magnifier.
        var ctx = {
          finishCode: opener.getAttribute('data-pdpcv-finish-code') || '',
          finishName: opener.getAttribute('data-pdpcv-finish-name') || '',
          finishImage: opener.getAttribute('data-pdpcv-finish-image') || '',
          finishDescription: opener.getAttribute('data-pdpcv-finish-description') || ''
        };
        open([primary].concat(extras), caption, ctx);
        return;
      }
      // v2.5 iter22: sample checkbox toggle inside the lightbox.
      var sampleCb = e.target.closest('[data-pdpcv-sample-checkbox]');
      if (sampleCb) {
        // Use the change event below for state — this handler just prevents bubbling.
        return;
      }
      if (e.target.closest('[data-pdpcv-lightbox-close]')) {
        e.preventDefault();
        close();
        return;
      }
      var thumb = e.target.closest('[data-pdpcv-lightbox-thumb]');
      if (thumb) {
        e.preventDefault();
        setActive(parseInt(thumb.getAttribute('data-pdpcv-lightbox-thumb'), 10) || 0);
        return;
      }
      var dot = e.target.closest('[data-pdpcv-lightbox-dot]');
      if (dot) {
        e.preventDefault();
        setActive(parseInt(dot.getAttribute('data-pdpcv-lightbox-dot'), 10) || 0);
        return;
      }
      // v2.5 iter32: desktop hover prev/next arrows. setActive() already wraps via modulo math
      // (activeIdx-1 from 0 → last, activeIdx+1 from last → 0), so the carousel loops naturally.
      if (e.target.closest('[data-pdpcv-lightbox-prev]')) {
        e.preventDefault();
        setActive(activeIdx - 1);
        return;
      }
      if (e.target.closest('[data-pdpcv-lightbox-next]')) {
        e.preventDefault();
        setActive(activeIdx + 1);
        return;
      }
    });
    document.addEventListener('keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') setActive(activeIdx + 1);
      else if (e.key === 'ArrowLeft') setActive(activeIdx - 1);
    });
    // Swipe support on mobile stage. >40px horizontal swipe → next/prev.
    if (stageEl) {
      var sx = 0, sy = 0;
      stageEl.addEventListener('touchstart', function (e) {
        if (!images.length || images.length < 2) return;
        var t = e.touches[0]; sx = t.clientX; sy = t.clientY;
      }, { passive: true });
      stageEl.addEventListener('touchend', function (e) {
        if (!images.length || images.length < 2) return;
        var t = (e.changedTouches && e.changedTouches[0]) || null;
        if (!t) return;
        var dx = t.clientX - sx, dy = t.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) setActive(activeIdx + 1);
          else setActive(activeIdx - 1);
        }
      }, { passive: true });
    }

    // v2.5 iter22: sample-request checkbox toggle inside the lightbox.
    var sampleCheckboxEl = lb.querySelector('[data-pdpcv-sample-checkbox]');
    var sampleNoteEl = lb.querySelector('[data-pdpcv-sample-note]');
    var sampleFailEl = lb.querySelector('[data-pdpcv-sample-fail]');
    if (sampleCheckboxEl) {
      sampleCheckboxEl.addEventListener('change', function () {
        var code = sampleCheckboxEl.dataset.finishCode || '';
        if (!code) return;
        if (sampleCheckboxEl.checked) {
          var ok = addFinishSample({
            code: code,
            name: sampleCheckboxEl.dataset.finishName || '',
            image: sampleCheckboxEl.dataset.finishImage || ''
          });
          if (!ok) {
            // v2.5 iter26: at max limit — revert the check, fade -note out, fade -fail in.
            // Both share the .pdpcv-lightbox-sample-state slot so no reflow. Toast still fires.
            sampleCheckboxEl.checked = false;
            if (sampleNoteEl) sampleNoteEl.classList.remove('is-visible');
            if (sampleFailEl) sampleFailEl.classList.add('is-visible');
            return;
          }
          if (sampleNoteEl) sampleNoteEl.classList.add('is-visible');
          if (sampleFailEl) sampleFailEl.classList.remove('is-visible');
        } else {
          removeFinishSample(code);
          if (sampleNoteEl) sampleNoteEl.classList.remove('is-visible');
          if (sampleFailEl) sampleFailEl.classList.remove('is-visible');
        }
      });
    }
  })();

  // v2.5 iter18: simpler attachments — single clickable link (Step 1 + Step 3 duplicate). Drag-and-drop removed.
  // Both inputs share `data-pdpcv-file-input`; both lists share `data-pdpcv-attachment-list`. Adding/removing
  // a file updates state.step3.attachments and re-renders ALL list instances.
  (function initAttachments() {
    var fileInputs = document.querySelectorAll('[data-pdpcv-file-input]');
    if (!fileInputs.length) return;

    function formatBytes(b) {
      if (b < 1024) return b + ' B';
      if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
      return (b / (1024 * 1024)).toFixed(1) + ' MB';
    }
    function renderAttachmentList() {
      var lists = document.querySelectorAll('[data-pdpcv-attachment-list]');
      lists.forEach(function (listEl) {
        listEl.innerHTML = '';
        (state.step3.attachments || []).forEach(function (a, i) {
          var li = document.createElement('li');
          var nameSpan = document.createElement('span');
          nameSpan.className = 'pdpcv-attachment-name';
          nameSpan.textContent = a.name;
          var sizeSpan = document.createElement('span');
          sizeSpan.className = 'pdpcv-attachment-size';
          sizeSpan.textContent = formatBytes(a.size);
          var rm = document.createElement('button');
          rm.type = 'button';
          rm.className = 'pdpcv-attachment-remove';
          rm.setAttribute('aria-label', 'Remove ' + a.name);
          rm.innerHTML = '<svg width="12" height="12" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>';
          rm.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            var entry = state.step3.attachments[i];
            state.step3.attachments.splice(i, 1);
            attachmentFiles.splice(i, 1); // v2.5 (2026-05-13): keep parallel array in sync
            if (entry && entry.idbKey) idbDelete(entry.idbKey);
            renderAttachmentList();
            saveCache();
            measureAndSetStepHeight(state.currentStep);
          });
          li.appendChild(nameSpan);
          li.appendChild(sizeSpan);
          li.appendChild(rm);
          listEl.appendChild(li);
        });
      });
    }
    function handleFiles(fileList) {
      if (!fileList || !fileList.length) return;
      if (!state.step3.attachments) state.step3.attachments = [];
      var added = 0;
      Array.prototype.forEach.call(fileList, function (f) {
        if (state.step3.attachments.length >= MAX_ATTACHMENTS) return;
        if (f.size > MAX_ATTACHMENT_BYTES) return;
        // v2.5 (2026-05-13): persist binary to IDB so refresh/navigate-away keeps
        // the file. idbKey unique per pick; lives on the cached metadata entry.
        var idbKey = 'attach-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
        state.step3.attachments.push({ name: f.name, size: f.size, type: f.type, idbKey: idbKey });
        attachmentFiles.push(f); // runtime ref (parallel to state metadata)
        idbPut(idbKey, f).catch(function (err) {
          if (window.console) console.warn('[PDPCV] IDB put failed for', f.name, err);
        });
        added++;
      });
      if (added > 0) {
        renderAttachmentList();
        draftSavedSuppressed = true;
        if (window.__pdpcvPendingColorMatch) {
          flashToast('File successfully uploaded. It will appear in the Step 3 summary.', 'success', 3000);
          window.__pdpcvPendingColorMatch = false;
        } else {
          flashUploadToast();
        }
        saveCache();
        setTimeout(function () { draftSavedSuppressed = false; }, 3200);
        measureAndSetStepHeight(state.currentStep);
      }
    }
    fileInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        handleFiles(input.files);
        // Reset value so re-selecting the same file fires change again
        input.value = '';
      });
    });
    renderAttachmentList();
  })();

  // Triggers
  // CUST2-03 CTA (PDP body) — uses [data-pdpcv-trigger] hook
  document.querySelectorAll('[data-pdpcv-trigger]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      openModal();
    });
  });

  // Sidebar "Start Your Custom Request" button — capture phase to run BEFORE existing modal-three handler.
  // Source: canonical plan §"Modal-three sidebar intercept".
  var sidebarBtn = document.querySelector('a.button.is-green.w-button[href="/pages/customization"]');
  if (sidebarBtn) {
    sidebarBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      openModal();
    }, { capture: true });
  }

  // Listen for PDP variant-selector changes (size/return/finish-card) so modal heading + subtitle + defaults stay in sync.
  bindPdpSelectorListeners();

  // C9 Round 5: re-measure step height on window resize so the frame adapts when viewport changes
  var resizeRaf = null;
  window.addEventListener('resize', function () {
    if (!isOpen()) return;
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(function () {
      if (state && state.currentStep) measureAndSetStepHeight(state.currentStep);
    });
  });

  // ---------- Public API ----------
  window.PDPCV = {
    open: openModal,
    close: closeModal,
    step: setStep,
    submit: submitForm,
    get state() { return state; },
    get data() { return data; },
    clearCache: clearCache
  };
})();


/* === ?customize=open URL sniffer ===
   Appended by .tmp/build_v265_theme.py 2026-06-08. When the URL contains
   ?customize=open we auto-open the customization modal once PDPCV is
   registered. Polls briefly because window.PDPCV is set inside the same
   IIFE but ordering vs. page-load timing varies. */
(function () {
  try {
    var params = new URLSearchParams(window.location.search);
    if (params.get('customize') !== 'open') return;
    var tries = 0;
    function tryOpen() {
      if (window.PDPCV && typeof window.PDPCV.open === 'function') {
        window.PDPCV.open();
        return;
      }
      if (tries++ < 30) setTimeout(tryOpen, 100);
    }
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setTimeout(tryOpen, 0);
    } else {
      document.addEventListener('DOMContentLoaded', tryOpen);
    }
  } catch (e) { /* ignore - any failure leaves PDP in normal state */ }
})();

